/**
 * PostureConnections — V96-2-8. Security posture connections on Setup.
 *
 * Four families (identity provider, cloud account, vulnerability scanning,
 * productivity suite). Each connected source shows status, last collection
 * and pass/fail check counts; each unconnected provider offers Connect.
 * Connect / fix / AWS role setup / scan upload / connection check open one
 * modal DS Drawer.
 *
 * API: /api/w/:slug/posture-connectors (list: any member; writes: owner or
 * admin — enforced server-side; `editable` only hides the controls).
 * Secrets are write-only: inputs are type=password, nothing is echoed back.
 */
import {
  connectorTargetField, formInput, formRequestBody, friendlyConnectorTarget, getConnectorManifest, isFormFieldRequired, kindsManagedBy,
  orderConnectorGroups, privilegeApplies, resolveFormTemplate, scopeDisplayName, splitFormList, validateFormValues, type ManifestForm, type ManifestFormField, type PrivilegeDeclaration,
} from '@aigovops/core';
import { createContext, Fragment, useCallback, useContext, useEffect, useRef, useState, type FormEvent, type ReactNode, type Ref } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Checkbox, Drawer, Field, Input, Select, Spinner, StatusGlyph, type StatusGlyphKind } from '@aigovops/design-system';
import {
  ApiError,
  createPostureConnector,
  confirmPostureConnectorPrivilege,
  getAwsRoleTemplate,
  getGcpWifTemplate,
  listPostureConnectors,
  listProviderSuggestions,
  runPostureConnector,
  setPostureConnectorStatus,
  updatePostureCredentials,
  startAzureConsent,
  disconnectAzureConsent, removePostureConnector,
  uploadVulnerabilityScan,
  type AwsRoleTemplate,
  type GcpWifTemplate,
  type PostureConnector,
  type PostureConnectorKind,
  type PostureConnectorPrivilege,
  type PostureCredentialBody,
  type PostureFieldError,
  type ScanFormat,
  type ScanUploadResult,
  completeAzureConsent,
} from '../lib/api';
import { runOnboardingConnector } from '../lib/lifecycle-api';
import { queryKeys, useInvalidate } from '../lib/query-keys';
import { AskAdmin } from './AskAdmin';
import './posture-connections.css';

// ---- static model ----------------------------------------------------------

// ADR-0051 / V101-1-4, G-1f/G-1g (D-CI-30): the kinds, labels, groups, group
// order and (for most kinds) the connect form all come from the connector
// manifest. This page keeps hand-written forms only for kinds with a real
// setup flow (BESPOKE_FORMS below).
const groupOf = (k: string) => getConnectorManifest(k)?.ui.group ?? 'Other';

/** Kinds with a hand-written form (BESPOKE_FORMS): real setup flows, not a field list. */
const BESPOKE_KINDS: ReadonlySet<string> = new Set(['okta_posture', 'entra_posture', 'okta', 'entra', 'microsoft_365', 'google_workspace', 'aws', 'gcp', 'vuln_scanner']);
const hasConnectForm = (kind: string): boolean => BESPOKE_KINDS.has(kind) || !!getConnectorManifest(kind)?.form;

/** Kinds this page has a connect form for: managed here, offered on Setup, with a manifest or bespoke form. */
const FORM_KINDS: readonly PostureConnectorKind[] = kindsManagedBy('posture_connectors')
  .filter((k) => getConnectorManifest(k)?.ui.setup !== false && hasConnectForm(k));

export const PROVIDER_LABEL: Record<PostureConnectorKind, string> = Object.fromEntries(
  FORM_KINDS.map((k) => [k, getConnectorManifest(k)?.ui.label ?? k])
);

/** Setup sections in CONNECTOR_GROUPS order (core), each with its kinds in registry order. */
export const POSTURE_FAMILIES: Array<{ id: string; title: string; kinds: PostureConnectorKind[] }> = orderConnectorGroups(FORM_KINDS.map(groupOf)).map((g) => ({
  ...g,
  kinds: FORM_KINDS.filter((k) => groupOf(k) === g.title),
}));

/** Every kind managed by this section (excluded from the generic Connections list). */
export const POSTURE_KIND_SET: ReadonlySet<string> = new Set(POSTURE_FAMILIES.flatMap(f => f.kinds));

type Tone = 'success' | 'warning' | 'danger' | 'neutral';

const STALE_MS = 7 * 86_400_000;
const POLL_MS = 5_000;
const POLL_MAX_MS = 120_000; // fast polling window
const SLOW_POLL_MS = 30_000;
const STALL_MS = 600_000; // after 10 min: "Still collecting — check back"

function parseTime(iso: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso.endsWith('Z') || /[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso.replace(' ', 'T')}Z`);
  return Number.isNaN(t) ? null : t;
}

/** "Couldn't collect N of M checks." with N < M means the connection works; only some checks couldn't run. */
export function partialRun(message: string | null | undefined): { failed: number; total: number } | null {
  const m = /^Couldn't collect (\d+) of (\d+) checks\./.exec(message ?? '');
  if (!m) return null;
  const failed = Number(m[1]); const total = Number(m[2]);
  return failed < total ? { failed, total } : null;
}

/** Re-confirmation within this window (or overdue) makes an admin-key connector need attention. */
const RECONFIRM_SOON_DAYS = 7;

export function connectorHealth(c: PostureConnector, opts: { collecting?: boolean; now?: number } = {}): { label: string; tone: Tone; glyph: StatusGlyphKind; key: 'collecting' | 'error' | 'partial' | 'paused' | 'reconfirm' | 'new' | 'stale' | 'connected' } {
  if (opts.collecting) return { key: 'collecting', label: 'Collecting…', tone: 'neutral', glyph: 'dots' };
  const partial = c.status !== 'error' ? partialRun(c.last_error_message) : null;
  if (partial) return { key: 'partial', label: `${partial.failed} ${partial.failed === 1 ? 'check needs' : 'checks need'} attention`, tone: 'warning', glyph: 'warning' };
  if (c.status === 'error' || c.last_error_message) return { key: 'error', label: 'Needs attention', tone: 'danger', glyph: 'alert' };
  if (c.status === 'disabled') return { key: 'paused', label: 'Paused', tone: 'neutral', glyph: 'pause' };
  if (c.privilege && privilegeDue(c.privilege, opts.now).days <= RECONFIRM_SOON_DAYS) return { key: 'reconfirm', label: 'Needs attention', tone: 'warning', glyph: 'warning' };
  if (!c.last_run_at) return { key: 'new', label: c.kind === 'vuln_scanner' ? 'No scan yet' : 'Waiting for first run', tone: 'neutral', glyph: 'circle' };
  const observed = parseTime(c.checks.last_observed_at) ?? parseTime(c.last_run_at);
  if (observed !== null && (opts.now ?? Date.now()) - observed > STALE_MS) return { key: 'stale', label: 'Stale', tone: 'warning', glyph: 'warning' };
  return { key: 'connected', label: 'Connected', tone: 'neutral', glyph: 'check' };
}

export function relativeTime(iso: string | null, now = Date.now()): string {
  const t = parseTime(iso);
  if (t === null) return iso ? 'unknown' : 'never';
  const mins = Math.max(0, Math.round((now - t) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/** A human-readable target; the raw ARN / GUID stays in the details disclosure. */
export function friendlyTarget(c: Pick<PostureConnector, 'kind' | 'target'>): string | null {
  if (!c.target) return null;
  if (c.kind === 'aws') {
    const m = /^arn:aws[a-z-]*:iam::(\d{12}):role\/(?:.*\/)?([^/]+)$/.exec(c.target);
    return m ? `Account …${m[1]!.slice(-4)} · ${m[2]}` : c.target;
  }
  if (c.kind === 'gcp') {
    const [type, id] = c.target.split('/');
    return type === 'organizations' ? `Organization …${(id ?? '').slice(-4)}` : `Project ${id ?? c.target}`;
  }
  // G-1h: every other kind displays its target as its manifest's ui.target says (host / tail4 / option / raw).
  const m = getConnectorManifest(c.kind);
  return m ? friendlyConnectorTarget(m, c.target) : c.target;
}

// ---- form model --------------------------------------------------------------

interface FieldSpec {
  key: string;
  label: string;
  kind: 'text' | 'secret' | 'select' | 'key-file';
  required: boolean;
  hint?: ReactNode | ((values: Record<string, string>) => ReactNode);
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
  /** Shown (and validated) only when this returns true. */
  when?: (values: Record<string, string>) => boolean;
  /** Scope names to offer "Copy scopes" for (shown after the field, outside its described-by hint). */
  copyScopes?: string[];
  /** Consecutive fields with the same group render in one fieldset (manifest `group`). */
  group?: { id: string; legend: string; collapsed?: boolean };
}

/** Consecutive fields sharing a group become one segment (rendered as a fieldset). */
function groupFields(fields: FieldSpec[]): Array<{ group?: FieldSpec['group']; fields: FieldSpec[] }> {
  const out: Array<{ group?: FieldSpec['group']; fields: FieldSpec[] }> = [];
  for (const f of fields) {
    const last = out[out.length - 1];
    if (last && f.group && last.group?.id === f.group.id) last.fields.push(f);
    else out.push({ group: f.group, fields: [f] });
  }
  return out;
}

/**
 * V101-3-6: credential fields in a `form.together` pair keep their stored value when
 * left blank on update — only when the connection already has the pair (a config
 * member of it is stored), so a connection without one is never told a secret exists.
 */
function keptOnUpdate(kind: string, connector: PostureConnector | undefined): Set<string> {
  const form = getConnectorManifest(kind)?.form;
  if (!form || !connector) return new Set();
  const stored = connector.form_values ?? {};
  const isCred = (k: string) => form.fields.find((f) => f.key === k)?.store === 'credential';
  // V101-3-3: every connection holds its required keys, so they may stay blank on update.
  const required = form.fields.filter((f) => f.store === 'credential' && f.required !== false).map((f) => f.key);
  return new Set([...required, ...(form.together ?? []).filter((set) => set.some((k) => !isCred(k) && stored[k])).flat().filter(isCred)]);
}

const isKeyAuth = (v: Record<string, string>) => v.auth === 'sa_key';
const fieldsFor = (kind: PostureConnectorKind, values: Record<string, string>) => formFieldsOf(kind).filter(f => !f.when || f.when(values));

const GUID_HINT = 'Format: 00000000-0000-0000-0000-000000000000';
const MS_APP_FIELDS: FieldSpec[] = [
  { key: 'tenant_id', label: 'Directory (tenant) ID', kind: 'text', required: true, hint: GUID_HINT },
  { key: 'client_id', label: 'Application (client) ID', kind: 'text', required: true, hint: GUID_HINT },
  { key: 'client_secret', label: 'Client secret', kind: 'secret', required: true },
];

const OKTA_FIELDS: FieldSpec[] = [
  { key: 'org_url', label: 'Okta org URL', kind: 'text', required: true, placeholder: 'https://yourcompany.okta.com' },
  { key: 'token', label: 'API token', kind: 'secret', required: true },
  { key: 'auth_scheme', label: 'Token type', kind: 'select', required: false, options: [{ value: 'SSWS', label: 'Okta API token' }, { value: 'Bearer', label: 'OAuth access token' }] },
];

/** "Create one" link for a credential (opens the vendor's page in a new tab). */
const vendorLink = (href: string, text: string) => {
  // Keep the last word and the glyph together so the icon never wraps alone.
  const i = text.lastIndexOf(' ');
  return <a href={href} target="_blank" rel="noreferrer">{i > 0 ? text.slice(0, i + 1) : ''}<span className="v101-nowrap">{i > 0 ? text.slice(i + 1) : text}<ExternalGlyph /></span><span className="sr-only"> (opens in a new tab)</span></a>;
};

function ExternalGlyph() {
  return <svg className="v101-ext" width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M9 3h4v4M13 3L7 9M11 9.5V13H3V5h3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

/**
 * Hand-written forms, kept only for kinds with a real setup flow: keyless
 * AWS/GCP, Workspace delegation, Okta, Microsoft Graph app registrations, and
 * the scan-upload connector. Every other kind renders its manifest `form`.
 */
const BESPOKE_FORMS: Record<string, FieldSpec[]> = {
  okta: OKTA_FIELDS,
  entra: MS_APP_FIELDS,
  okta_posture: [
    { key: 'org_url', label: 'Okta org URL', kind: 'text', required: true, placeholder: 'https://yourcompany.okta.com' },
    { key: 'token', label: 'API token', kind: 'secret', required: true },
    { key: 'auth_scheme', label: 'Token type', kind: 'select', required: false, options: [{ value: 'SSWS', label: 'Okta API token' }, { value: 'Bearer', label: 'OAuth access token' }] },
  ],
  entra_posture: MS_APP_FIELDS,
  microsoft_365: MS_APP_FIELDS,
  google_workspace: [
    { key: 'auth', label: 'Sign-in method', kind: 'select', required: true, options: [{ value: 'wif', label: 'Keyless (recommended)' }, { value: 'sa_key', label: 'Service account key' }] },
    { key: 'subject', label: 'Admin email', kind: 'text', required: true, placeholder: 'admin@yourcompany.com', hint: 'A super admin GAC Pro reads as (read-only scopes).' },
    { key: 'host_project_id', label: 'Host project ID', kind: 'text', required: true, placeholder: 'your-security-project', hint: <>A Google Cloud project in your organization; GAC Pro’s keyless service account is created here. No project yet? <a href="https://console.cloud.google.com/projectcreate" target="_blank" rel="noreferrer">Create one</a>.</>, when: v => !isKeyAuth(v) },
    { key: 'host_project_number', label: 'Host project number', kind: 'text', required: true, placeholder: '123456789012', hint: 'On the project’s dashboard in the Google Cloud console.', when: v => !isKeyAuth(v) },
    { key: 'service_account', label: 'Service account key file (.json)', kind: 'key-file', required: true, when: isKeyAuth },
  ],
  aws: [
    { key: 'role_arn', label: 'Role ARN', kind: 'text', required: true, placeholder: 'arn:aws:iam::123456789012:role/GacProReadOnly' },
    { key: 'regions', label: 'Regions', kind: 'text', required: true, placeholder: 'us-east-1, us-west-2', hint: 'Comma-separated.' },
    // V101-3-6 (D-CI-35): GAC Pro lists file names and times there; it never opens a file.
    { key: 'pinecone_audit_s3', label: 'Pinecone audit log location (optional)', kind: 'text', required: false, placeholder: 'my-bucket/pinecone-logs',
      hint: 'Bucket and folder as in Pinecone’s audit log settings, e.g. my-bucket/pinecone-logs. GAC Pro lists file names there and checks one arrived in the last 24 hours.',
      when: v => v._has_pinecone === '1' || !!v.pinecone_audit_s3 },
  ],
  gcp: [
    { key: 'auth', label: 'Sign-in method', kind: 'select', required: true, options: [{ value: 'wif', label: 'Workload identity (recommended)' }, { value: 'sa_key', label: 'Service account key' }] },
    { key: 'scope', label: 'Organization or project to read', kind: 'text', required: true, placeholder: 'organizations/123456789012', hint: 'Or projects/your-project-id.' },
    { key: 'host_project_id', label: 'Host project ID', kind: 'text', required: true, placeholder: 'your-security-project', hint: 'GAC Pro’s read-only service account is created here.', when: v => !isKeyAuth(v) },
    { key: 'host_project_number', label: 'Host project number', kind: 'text', required: true, placeholder: '123456789012', hint: 'On the project’s dashboard in the Google Cloud console.', when: v => !isKeyAuth(v) },
    { key: 'service_account', label: 'Service account key file (.json)', kind: 'key-file', required: true, when: isKeyAuth },
  ],
  vuln_scanner: [],
};

/** A manifest hint: text, code chips and vendor links (link templates read the form's current values). */
function renderHint(form: ManifestForm, f: ManifestFormField, values: Record<string, string>): ReactNode {
  const parts = typeof f.hint === 'string' ? [f.hint] : f.hint ?? [];
  const nodes = parts.map((p, i) => {
    const prev = parts[i - 1];
    const sep = typeof p !== 'string' && 'code' in p && prev && typeof prev !== 'string' && 'code' in prev ? ', ' : null;
    return <Fragment key={i}>{sep && <span className="sr-only">{sep}</span>}{typeof p === 'string' ? p : 'code' in p ? <code>{breakable(p.code)}</code> : vendorLink(resolveFormTemplate(form, p.link.href, values), p.link.text)}</Fragment>;
  });
  // V104-D-4: a hint that lists scope names offers to copy them (as Google Workspace's setup does).
  return f.hintStyle === 'scopes' ? <span className="v101-hint-scopes">{nodes}</span> : <>{nodes}</>;
}

/** Scope names a manifest hint lists as code chips, when there are enough of them to be worth copying. */
function scopesOf(f: ManifestFormField): string[] | undefined {
  const names = typeof f.hint === 'string' || !f.hint ? [] : f.hint.flatMap(p => (typeof p !== 'string' && 'code' in p ? [p.code] : []));
  return f.hintStyle === 'scopes' && names.length > 1 ? names : undefined;
}

/** A copy status that clears itself, so it is not left in the reading order. */
function useTransient(ms = 6000): [string, (m: string) => void] {
  const [text, setText] = useState('');
  useEffect(() => { if (!text) return; const t = window.setTimeout(() => setText(''), ms); return () => window.clearTimeout(t); }, [text, ms]);
  return [text, setText];
}

function CopyScopes({ scopes }: { scopes: string[] }) {
  const [status, setStatus] = useTransient();
  const copy = async () => {
    setStatus('');
    try { await navigator.clipboard.writeText(scopes.join(', ')); window.setTimeout(() => setStatus('Scopes copied.'), 50); }
    catch { window.setTimeout(() => setStatus('Copy was unavailable. Select the scope names above and copy them.'), 50); }
  };
  return <span className="v101-copy-scopes"><Button variant="secondary" compact onClick={() => void copy()}>Copy scopes</Button><span role="status">{status}</span></span>;
}

/** G-1g: the connect form from the manifest `form` schema (the same schema the server validates with). */
function manifestFields(kind: string): FieldSpec[] {
  const form = getConnectorManifest(kind)?.form;
  if (!form) return [];
  return form.fields.map((f): FieldSpec => {
    const input = formInput(f);
    return {
      key: f.key, label: f.label, required: isFormFieldRequired(f), placeholder: f.placeholder,
      kind: input === 'secret' ? 'secret' : input === 'select' ? 'select' : 'text',
      options: f.options?.map(o => ({ value: o.value, label: o.label })),
      hint: f.hint ? (values: Record<string, string>) => renderHint(form, f, values) : undefined,
      group: f.group, copyScopes: scopesOf(f),
    };
  });
}

const formFieldsOf = (kind: string): FieldSpec[] => (BESPOKE_KINDS.has(kind) ? BESPOKE_FORMS[kind] ?? [] : manifestFields(kind));
/** The manifest form, for kinds without a hand-written one. */
const manifestFormOf = (kind: string) => (BESPOKE_KINDS.has(kind) ? undefined : getConnectorManifest(kind)?.form);

const GUID_RX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Client-side checks that mirror the server's; the server stays authoritative. */
export function validatePostureForm(kind: PostureConnectorKind, values: Record<string, string>, kept?: ReadonlySet<string>): Record<string, string> {
  // G-1g: manifest-form kinds use the shared rules (the server applies the same ones).
  const m = getConnectorManifest(kind);
  if (m && manifestFormOf(kind)) {
    const found = validateFormValues(m, values, kept);
    // V104-D-5: a log index without the workspace ID + compliance key is never read; say so instead of dropping it silently.
    if (kind === 'openai' && (values.chatgpt_log_index ?? '').trim() && !(values.chatgpt_workspace_id ?? '').trim() && !found.chatgpt_log_index) {
      found.chatgpt_log_index = 'Add the ChatGPT workspace ID and compliance key too, or clear this index.';
    }
    return found;
  }
  const errors: Record<string, string> = {};
  for (const f of fieldsFor(kind, values)) {
    if (f.kind === 'key-file') {
      if (!values.client_email || !values.private_key) errors[f.key] = 'Choose the service account key file.';
      continue;
    }
    if (f.required && !(values[f.key] ?? '').trim()) errors[f.key] = `${f.label} is required.`;
  }
  if ((kind === 'entra_posture' || kind === 'entra' || kind === 'microsoft_365')) {
    for (const k of ['tenant_id', 'client_id']) if (values[k]?.trim() && !GUID_RX.test(values[k].trim())) errors[k] = 'This must be a GUID.';
  }
  if ((kind === 'okta_posture' || kind === 'okta') && values.org_url?.trim() && !/^https:\/\//i.test(values.org_url.trim())) errors.org_url = 'Use the https:// address of your Okta org.';
  if (kind === 'aws' && values.role_arn?.trim() && !/^arn:aws[a-z-]*:iam::\d{12}:role\//.test(values.role_arn.trim())) errors.role_arn = 'Use the ARN of an IAM role, e.g. arn:aws:iam::123456789012:role/Name.';
  if (kind === 'gcp' || kind === 'google_workspace') {
    if (kind === 'gcp' && values.scope?.trim() && !/^(organizations\/\d+|projects\/[a-z][a-z0-9-]{4,28}[a-z0-9])$/.test(values.scope.trim())) errors.scope = 'Use organizations/<number> or projects/<project-id>.';
    if (!isKeyAuth(values) && values.host_project_id?.trim() && !/^[a-z][a-z0-9-]{4,28}[a-z0-9]$/.test(values.host_project_id.trim())) errors.host_project_id = 'Use the project ID (lowercase letters, digits and hyphens), not its name.';
    if (!isKeyAuth(values) && values.host_project_number?.trim() && !/^\d{6,20}$/.test(values.host_project_number.trim())) errors.host_project_number = 'The project number is digits only.';
  }
  return errors;
}

function toBody(kind: PostureConnectorKind, values: Record<string, string>): PostureCredentialBody {
  const body: PostureCredentialBody = {};
  if (values.name?.trim()) body.name = values.name.trim();
  const form = manifestFormOf(kind);
  if (form) return { ...body, ...formRequestBody(form, values) };
  switch (kind) {
    case 'okta':
    case 'okta_posture':
      return { ...body, org_url: values.org_url.trim(), token: values.token, auth_scheme: values.auth_scheme || 'SSWS' };
    case 'entra':
    case 'entra_posture':
    case 'microsoft_365':
      return { ...body, tenant_id: values.tenant_id.trim(), client_id: values.client_id.trim(), client_secret: values.client_secret };
    case 'google_workspace':
      return isKeyAuth(values)
        ? { ...body, auth: 'sa_key', client_email: values.client_email, private_key: values.private_key, subject: values.subject.trim(), ...(/^[0-9a-f]{40}$/.test(values.key_id ?? '') ? { key_id: values.key_id } : {}) }
        : { ...body, auth: 'wif', subject: values.subject.trim(), host_project_id: values.host_project_id.trim(), host_project_number: values.host_project_number.trim() };
    case 'aws':
      return { ...body, role_arn: values.role_arn.trim(), regions: values.regions.split(',').map(r => r.trim()).filter(Boolean), pinecone_audit_s3: (values.pinecone_audit_s3 ?? '').trim() };
    case 'gcp':
      return isKeyAuth(values)
        ? { ...body, auth: 'sa_key', scope: values.scope.trim(), service_account_email: values.client_email, private_key: values.private_key, ...(/^[0-9a-f]{40}$/.test(values.key_id ?? '') ? { key_id: values.key_id } : {}) }
        : { ...body, auth: 'wif', scope: values.scope.trim(), host_project_id: values.host_project_id.trim(), host_project_number: values.host_project_number.trim() };
    default:
      return body;
  }
}

/** Split server 422 errors into per-field messages and the rest (form-level). */
export function splitServerErrors(kind: PostureConnectorKind, errors: PostureFieldError[] | undefined): { fields: Record<string, string>; rest: string[] } {
  const known = new Set([...formFieldsOf(kind).map(f => f.key), 'name']);
  const fields: Record<string, string> = {};
  const rest: string[] = [];
  for (const e of errors ?? []) {
    let key = e.path.split('.')[0] ?? '';
    if (kind === 'google_workspace' && (key === 'client_email' || key === 'private_key')) key = 'service_account';
    if (kind === 'gcp' && key === 'private_key') key = 'service_account';
    if (key && known.has(key)) { if (!fields[key]) fields[key] = e.message; }
    else rest.push(e.path ? `${e.path}: ${e.message}` : e.message);
  }
  return { fields, rest };
}

export function scanFormatFor(fileName: string): ScanFormat | null {
  const n = fileName.toLowerCase();
  if (n.endsWith('.csv')) return 'csv';
  if (n.endsWith('.sarif') || n.endsWith('.json')) return 'sarif';
  return null;
}

/** Friendly text for collector run failures (the server says 422 when not active, 503 when collection is off). */
export function friendlyRunError(e: unknown): string {
  if (e instanceof ApiError && e.status === 422) return 'This source is paused — resume it first.';
  if (e instanceof ApiError && e.status === 503) return "Collection isn't available right now — try again later.";
  return e instanceof Error ? e.message : 'That did not work. Please retry.';
}

/**
 * BL-17: the Pinecone audit-log check (run on the AWS connection) names its location as
 * `s3://bucket/folder/` in its own reason line, so that line is the one to show next to the
 * AWS field. Returns it, or null when the message has no such reason.
 */
export function auditLogReason(message: string | null | undefined): string | null {
  return (message ?? '').split('\n').map(l => l.trim()).find(l => l.includes('s3://')) ?? null;
}

// ---- component ---------------------------------------------------------------

type Panel =
  | { mode: 'connect'; kind: PostureConnectorKind; consent?: string; consentWorkspace?: { slug: string; name: string }; prefill?: string; focus?: string; intent?: 'add-chatgpt' }
  | { mode: 'azure'; connector: PostureConnector }
  | { mode: 'disconnect'; connector: PostureConnector }
  | { mode: 'remove'; connector: PostureConnector }
  | { mode: 'fix'; connector: PostureConnector; intent?: 'switch' | 'replace-key' | 'add-chatgpt'; focus?: string }
  | { mode: 'check'; connector: PostureConnector }
  | { mode: 'role'; connector: PostureConnector; template?: AwsRoleTemplate }
  | { mode: 'gcp'; connector: PostureConnector; template?: GcpWifTemplate; retireKey?: string[] }
  | { mode: 'upload'; connector: PostureConnector }
  | { mode: 'reconfirm'; connector: PostureConnector }
  /** ADR-0102 §6: add the scopes a manifest version added (the row's "Update permissions"). */
  | { mode: 'regrant'; connector: PostureConnector };

/** ADR-0053 §2: when a declared over-privilege is next due for re-confirmation. */
export function privilegeDue(p: { confirmed_at: string; revalidateDays: number }, now = Date.now()): { days: number; overdue: boolean; label: string } {
  const due = (parseTime(p.confirmed_at) ?? now) + p.revalidateDays * 86_400_000;
  const days = Math.ceil((due - now) / 86_400_000);
  if (days <= 0) return { days, overdue: true, label: 'Re-confirmation overdue' };
  return { days, overdue: false, label: `Re-confirm due in ${days} ${days === 1 ? 'day' : 'days'}` };
}

/**
 * One collection run being tracked for a connector. Lives in the panel (not
 * the drawer) so tracking survives closing the drawer. `running` polls the
 * list every 5 s for 2 min, then every 30 s up to 10 min, then `stalled`.
 */
export interface Tracking { since: number; baseline: string | null; errorBaseline: string | null; phase: 'running' | 'ok' | 'partial' | 'error' | 'stalled'; message?: string }

interface TrackApi {
  tracking: Record<string, Tracking>;
  /** Start a run (PATCH active first if the connector is in error); re-polls instead when one is already in flight. */
  collect: (c: PostureConnector) => Promise<void>;
  /** Re-poll without starting a new run. */
  repoll: (c: PostureConnector) => void;
  /** Track a run the server already started (e.g. a 202 scan upload). */
  watch: (c: PostureConnector) => void;
}

/** True when this section has a connect form for `kind` (the target of `connectKind` / `?connect=`). */
export function isPostureFormKind(kind: string | null | undefined): kind is PostureConnectorKind {
  return !!kind && (FORM_KINDS as readonly string[]).includes(kind);
}

/**
 * One line for an over-privileged connector, for the card badge. V109-1-8 (PD-5): every kind whose
 * manifest declares a credential beyond read-only says so plainly; it is never called read-only.
 */
export function privilegeBadgeLabel(kind: string): string {
  return getConnectorManifest(kind)?.privilege ? 'Uses an admin key' : 'Broader access';
}

/** V109-1-8: the kind always uses an admin key (declared, and not only for optional fields). */
export function alwaysAdminKey(kind: string): boolean {
  const p = getConnectorManifest(kind)?.privilege;
  return !!p && !p.onlyWithFields?.length;
}

/**
 * V108-6-4: on Setup the section sits under the setup steps, whose open row holds the page's one filled
 * primary; `quiet` renders the rows' own primaries as secondary (the drawer keeps its submit primary).
 */
const QuietRows = createContext(false);

/**
 * `connectKind` opens the connect drawer for that kind (owner/admin only) —
 * Setup passes `?connect=<kind>` through it; `onConnectOpened` lets the caller
 * clear the param so the same kind can be opened again.
 */
export function PostureConnections({ slug, editable, connectKind, onConnectOpened, showSuggested, back, drawerOnly, onDrawerClosed, returnFocusTo, quiet = false }: {
  slug: string; editable: boolean; connectKind?: string | null; onConnectOpened?: () => void;
  /** V108-6-2: onboarding's Grant step opens the same connect drawer without the section around it. */
  drawerOnly?: boolean; onDrawerClosed?: () => void; returnFocusTo?: () => HTMLElement | null;
  quiet?: boolean;
  /** Setup: kinds already offered under Suggested connections show a "Suggested" marker instead of a second Connect. */
  showSuggested?: boolean;
  /** Where the user came from (a system), offered after connecting. */
  back?: { to: string; label: string } | null;
}) {
  const [tracking, setTracking] = useState<Record<string, Tracking>>({});
  const running = Object.values(tracking).filter(t => t.phase === 'running');
  const fast = running.some(t => Date.now() - t.since < POLL_MAX_MS);
  const query = useQuery({
    queryKey: queryKeys.postureConnectors(slug), queryFn: () => listPostureConnectors(slug), enabled: !!slug,
    placeholderData: keepPreviousData, refetchInterval: running.length ? (fast ? POLL_MS : SLOW_POLL_MS) : false,
  });
  const queryClient = useQueryClient();
  const invalidate = useInvalidate();
  const location = useLocation();
  const [panel, setPanel] = useState<Panel | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const closePanel = useCallback(() => { setPanel(null); onDrawerClosed?.(); }, [onDrawerClosed]);
  const refresh = useCallback(() => { void queryClient.invalidateQueries({ queryKey: queryKeys.postureConnectors(slug) }); invalidate('connector:changed', slug); }, [queryClient, slug, invalidate]);
  // The announcers re-set their live region after 50 ms; clear pending timers on unmount so none fires on an
  // unmounted component (the ITSM tests saw it fire after teardown as an unhandled error, 2026-10-22).
  const pendingTimers = useRef<number[]>([]);
  useEffect(() => () => { pendingTimers.current.forEach((t) => window.clearTimeout(t)); pendingTimers.current = []; }, []);
  const later = useCallback((fn: () => void, ms: number) => { pendingTimers.current.push(window.setTimeout(fn, ms)); }, []);
  const announcePage = useCallback((message: string) => { setNotice(''); later(() => setNotice(message), 50); }, [later]);
  // While the modal drawer is open the page is inert, so results go to the drawer's own live region.
  const [drawerLive, setDrawerLive] = useState('');
  const sayDrawer = useCallback((message: string) => { setDrawerLive(''); later(() => setDrawerLive(message), 50); }, [later]);
  const panelOpen = !!panel;
  const announce = useCallback((message: string) => (panelOpen ? sayDrawer : announcePage)(message), [panelOpen, sayDrawer, announcePage]);

  const connectors = query.data?.connectors ?? [];
  const suggestions = useQuery({ queryKey: queryKeys.providerSuggestions(slug), queryFn: () => listProviderSuggestions(slug), enabled: !!slug && !!showSuggested && editable });
  const suggestedKinds = new Set((suggestions.data?.suggestions ?? []).map(x => x.connector_kind));

  // External open (suggestion → Connect): the same drawer the family buttons open.
  // ADR-0062: the Microsoft consent callback lands here with ?connect=azure_openai&azure_consent=finish
  // and the request's state + code in the URL fragment. This page is same-site, so posting them
  // carries the session (security review D-1); the reply is the result the drawer explains.
  const pendingConsent = useRef<string | null>(null);
  const finishing = useRef(false);
  useEffect(() => {
    if (!connectKind) return;
    const consent = connectKind === 'azure_openai' ? new URLSearchParams(location.search).get('azure_consent') : null;
    if (consent === 'finish') {
      if (finishing.current) return;
      finishing.current = true;
      const frag = new URLSearchParams(window.location.hash.slice(1));
      // The code is single use; drop it from the address bar and history straight away.
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
      const state = frag.get('state');
      const show = (result: string, consentWorkspace?: { slug: string; name: string }) => { if (editable) setPanel({ mode: 'connect', kind: 'azure_openai', consent: result, ...(consentWorkspace ? { consentWorkspace } : {}) }); };
      if (!state) show('signed_out');
      else void completeAzureConsent(slug, { state, ...(frag.get('code') ? { code: frag.get('code')! } : {}), ...(frag.get('error') ? { error: true } : {}) })
        .then((r) => {
          if (r.result === 'connected' && r.connector_id) { pendingConsent.current = r.connector_id; refresh(); }
          else show(r.result, r.other_workspace_slug && r.other_workspace_name ? { slug: r.other_workspace_slug, name: r.other_workspace_name } : undefined);
        })
        .catch(() => show('failed'));
      onConnectOpened?.();
      return;
    }
    if (editable && consent === 'connected') pendingConsent.current = new URLSearchParams(location.search).get('connector');
    else if (editable && isPostureFormKind(connectKind)) setPanel({ mode: 'connect', kind: connectKind, ...(consent ? { consent } : {}) });
    onConnectOpened?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectKind, editable, onConnectOpened]);
  useEffect(() => {
    const id = pendingConsent.current;
    if (!id || !query.data) return;
    const row = query.data.connectors.find(x => x.connector_id === id);
    if (!row) return;
    pendingConsent.current = null;
    setPanel({ mode: 'azure', connector: row });
    sayDrawer('Microsoft approved GAC Pro. Give it the read roles below, then check the connection.');
  }, [query.data, sayDrawer]);

  // Resolve running collections when the run lands, errors, or stalls.
  useEffect(() => {
    if (!query.data) return;
    const now = Date.now();
    const updates: Record<string, Tracking> = {};
    for (const [id, t] of Object.entries(tracking)) {
      if (t.phase !== 'running') continue;
      const row = query.data.connectors.find(x => x.connector_id === id);
      const label = row ? PROVIDER_LABEL[row.kind] : 'Connection';
      if (!row) continue;
      if (row.last_error_message && row.last_error_at !== t.errorBaseline) {
        updates[id] = { ...t, phase: partialRun(row.last_error_message) ? 'partial' : 'error', message: row.last_error_message };
        announce(`${label}: collection failed. ${plainText(row.last_error_message)}`);
      } else if (row.last_run_at !== t.baseline) {
        updates[id] = { ...t, phase: 'ok', message: `${row.checks.pass} passing · ${row.checks.fail} failing` };
        announce(`${label}: collection finished — ${row.checks.pass} passing, ${row.checks.fail} failing.`);
      } else if (now - t.since > STALL_MS) {
        updates[id] = { ...t, phase: 'stalled' };
        announce(`${label}: still collecting. Check back later.`);
      }
    }
    if (Object.keys(updates).length) setTracking(prev => ({ ...prev, ...updates }));
  }, [query.data, tracking, announce]);

  // Deep links: /setup#security-posture (section), #posture-families (the families grid) or #posture-<connector_id> (one card).
  const scrolled = useRef<string | null>(null);
  useEffect(() => {
    if (!query.data || scrolled.current === location.hash) return;
    const row = /^#posture-[A-Za-z0-9_-]+$/.test(location.hash) && location.hash !== '#posture-families' ? document.getElementById(location.hash.slice(1)) : null;
    if (row) {
      scrolled.current = location.hash;
      row.scrollIntoView?.({ block: 'center' });
      row.querySelector<HTMLElement>('[data-primary], button, a')?.focus();
      return;
    }
    if (location.hash !== '#security-posture' && location.hash !== '#posture-families') return;
    scrolled.current = location.hash;
    heading.current?.scrollIntoView?.({ block: 'start' });
    heading.current?.focus();
  }, [location.hash, query.data]);

  const track: TrackApi = {
    tracking,
    collect: async (c) => {
      const t = tracking[c.connector_id];
      if (t?.phase === 'running' || t?.phase === 'stalled') { track.repoll(c); return; }
      if (c.status === 'error') await setPostureConnectorStatus(slug, c.connector_id, 'active');
      // AI app discovery runs in the grant collector; posture kinds in the posture workflow.
      if (c.kind === 'okta' || c.kind === 'entra') await runOnboardingConnector(slug, c.connector_id);
      else await runPostureConnector(slug, c.connector_id);
      setTracking(prev => ({ ...prev, [c.connector_id]: { since: Date.now(), baseline: c.last_run_at, errorBaseline: c.last_error_at, phase: 'running' } }));
      refresh();
    },
    watch: (c) => {
      setTracking(prev => ({ ...prev, [c.connector_id]: { since: Date.now(), baseline: c.last_run_at, errorBaseline: c.last_error_at, phase: 'running' } }));
    },
    repoll: (c) => {
      setTracking(prev => {
        const t = prev[c.connector_id];
        return t ? { ...prev, [c.connector_id]: { ...t, since: Date.now(), phase: 'running' } } : prev;
      });
      void query.refetch();
    },
  };

  const act = async (key: string, op: () => Promise<string | void>) => {
    if (busy) return;
    setBusy(key); setActionError(null);
    try { const m = await op(); if (m) announce(m); refresh(); }
    catch (e) { setActionError(friendlyRunError(e)); }
    finally { setBusy(null); }
  };

  const broken = connectors.filter(c => { const k = connectorHealth(c).key; return (k === 'error' || k === 'reconfirm') && tracking[c.connector_id]?.phase !== 'running'; });
  // A reason on one row can name another connection ("Connect Datadog…", "Collect Datadog…").
  const hasKind = (k: string) => connectors.some(x => x.kind === k);
  // V104-D-5 (BL-22): OpenAI is checked again by itself once the Datadog step the user started from its partial box finishes.
  const rerunAfterDatadog = useRef<{ fromId: string; at: number } | null>(null);
  useEffect(() => {
    const pending = rerunAfterDatadog.current;
    if (!pending || !query.data) return;
    if (Date.now() - pending.at > 10 * 60_000) { rerunAfterDatadog.current = null; return; }
    for (const [id, t] of Object.entries(tracking)) {
      if (t.since < pending.at || !query.data.connectors.some(x => x.connector_id === id && x.kind === 'datadog')) continue;
      if (t.phase === 'ok' || t.phase === 'partial') {
        rerunAfterDatadog.current = null;
        const from = query.data.connectors.find(x => x.connector_id === pending.fromId);
        if (!from) return;
        announce('Datadog collected. Checking OpenAI again…');
        void track.collect(from).catch(e => announce(friendlyRunError(e)));
        return;
      }
      if (t.phase === 'error' || t.phase === 'stalled') {
        rerunAfterDatadog.current = null;
        announce('Datadog could not be collected, so OpenAI was not checked again.');
        return;
      }
    }
  }, [tracking, query.data]);
  const goToKind = (k: PostureConnectorKind, fromId?: string) => {
    const target = connectors.find(x => x.kind === k);
    if (fromId && k === 'datadog') {
      rerunAfterDatadog.current = { fromId, at: Date.now() };
      (target ? announcePage : sayDrawer)('After Datadog is collected, GAC Pro checks OpenAI again.');
    }
    if (!target) { setPanel({ mode: 'connect', kind: k }); return; }
    const row = document.getElementById(`posture-${target.connector_id}`);
    row?.scrollIntoView?.({ block: 'center' });
    row?.querySelector<HTMLElement>('[data-primary], .v96-row-actions button')?.focus();
  };
  // BL-17: the Pinecone audit-log location is an AWS field; go there from a Pinecone row.
  const openAuditLog = () => {
    const aws = connectors.find(x => x.kind === 'aws');
    setPanel(aws ? { mode: 'fix', connector: aws, focus: 'pinecone_audit_s3' } : { mode: 'connect', kind: 'aws', focus: 'pinecone_audit_s3' });
  };
  const focusFirstBroken = () => {
    const first = broken[0];
    if (!first) return;
    const row = document.getElementById(`posture-${first.connector_id}`);
    row?.scrollIntoView?.({ block: 'center' });
    row?.querySelector<HTMLElement>('[data-primary]')?.focus();
  };

  if (drawerOnly) {
    return <>
      <div role="status" aria-live="polite" className="sr-only">{notice}</div>
      {panel && editable && <PosturePanel slug={slug} panel={panel} onClose={closePanel} onPanel={setPanel} returnFocus={returnFocusTo ?? (() => null)}
        track={track} live={drawerLive} say={sayDrawer} onDone={() => refresh()} onPageMessage={announcePage} back={back ?? null} />}
    </>;
  }
  return <QuietRows.Provider value={quiet}><Card><section id="security-posture" className="v75-panel v96-posture" aria-labelledby="posture-title">
    <div className="v75-page-heading">
      <div>
        <div className="v96-title-line">
          <h2 id="posture-title" ref={heading} tabIndex={-1}>Security posture</h2>
          {/* V109-1-8 (PD-5): admin-key connections are never covered by the read-only claim. */}
          <Badge className="v75-status neutral"><LockGlyph /> {connectors.some(c => !!c.privilege || alwaysAdminKey(c.kind)) ? 'Read-only, except admin-key connections' : 'Read-only access'}</Badge>
        </div>
        {query.data && <p className="v75-muted">
          {connectors.length} connected{broken.length > 0 ? <> · <button type="button" className="v96-attn" onClick={focusFirstBroken}>{broken.length} need{broken.length === 1 ? 's' : ''} attention</button></> : null}
        </p>}
      </div>
    </div>
    <div role="status" aria-live="polite" className={notice ? 'v96-notice' : 'sr-only'}>{notice}</div>
    {actionError && <Alert variant="error" onDismiss={() => setActionError(null)}>{actionError}</Alert>}
    {query.isLoading ? <Spinner label="Loading security posture connections" />
      : query.error && !query.data ? <Alert variant="error"><p>Security posture connections could not load.</p><Button onClick={() => void query.refetch()}>Retry</Button></Alert>
      : <div className="v96-families" id="posture-families">
        {POSTURE_FAMILIES.map(family => {
          const connected = connectors.filter(c => family.kinds.includes(c.kind));
          const missing = family.kinds.filter(k => !connected.some(c => c.kind === k));
          return <section key={family.id} className="v96-family" aria-labelledby={`posture-${family.id}`}>
            <h3 id={`posture-${family.id}`}>{family.title}</h3>
            {connected.length > 0 && <ul className="v96-sources">
              {connected.map(c => <ConnectorRow key={c.connector_id} slug={slug} connector={c} editable={editable} busy={busy} tracking={tracking[c.connector_id]} onGoTo={goToKind} hasKind={hasKind} onAuditLog={openAuditLog}
                onPanel={setPanel}
                onRun={() => act(`run:${c.connector_id}`, async () => { await track.collect(c); return `Collection queued for ${PROVIDER_LABEL[c.kind]}.`; })}
                onRefresh={() => track.repoll(c)}
                onToggle={() => act(`toggle:${c.connector_id}`, async () => { const next = c.status === 'disabled' ? 'active' : 'disabled'; await setPostureConnectorStatus(slug, c.connector_id, next); return `${PROVIDER_LABEL[c.kind]} ${next === 'active' ? 'resumed' : 'paused'}.`; })}
              />)}
            </ul>}
            {missing.length > 0 && <div className={connected.length ? 'v96-add' : 'v96-empty'}>
              {connected.length === 0 && <span className="v96-empty-label"><StatusGlyph kind="circle" /> Not connected</span>}
              {editable
                ? <div className="v75-actions">{missing.map(k => suggestedKinds.has(k)
                  ? <Button key={k} variant="secondary" className="v101-suggested" onClick={() => setPanel({ mode: 'connect', kind: k })}>{`Connect ${PROVIDER_LABEL[k]}`} <Badge>Suggested</Badge>{alwaysAdminKey(k) && <> <Badge>{privilegeBadgeLabel(k)}</Badge></>}</Button>
                  : <Button key={k} variant="secondary" onClick={() => setPanel({ mode: 'connect', kind: k })}>{`Connect ${PROVIDER_LABEL[k]}`}{alwaysAdminKey(k) && <> <Badge>{privilegeBadgeLabel(k)}</Badge></>}</Button>)}</div>
                : connected.length === 0 && <span className="v75-muted">{missing.map(k => PROVIDER_LABEL[k]).join(' or ')}</span>}
              {/* V104-D-5: ChatGPT Enterprise rides on the OpenAI connection; with none, offer it by its own name. */}
              {editable && missing.includes('openai') && <div className="v75-actions"><Button variant="secondary" onClick={() => setPanel({ mode: 'connect', kind: 'openai', intent: 'add-chatgpt' })}>Connect ChatGPT Enterprise</Button></div>}
            </div>}
          </section>;
        })}
      </div>}
    {!editable && <AskAdmin slug={slug} what="manages security posture connections" subject="Security posture connections in GAC Pro" />}
    {panel && editable && <PosturePanel slug={slug} panel={panel} onClose={closePanel} onPanel={setPanel} returnFocus={() => heading.current}
      track={track} live={drawerLive} say={sayDrawer} onDone={() => refresh()} onPageMessage={announcePage} back={back ?? null} />}
  </section></Card></QuietRows.Provider>;
}

function LockGlyph() {
  return <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true"><rect x="3" y="7" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" /><path d="M5.5 7V5a2.5 2.5 0 015 0v2" stroke="currentColor" strokeWidth="1.5" /></svg>;
}

/**
 * Row-level secondary actions in a disclosure menu. Enter/Space toggles;
 * Escape or focus leaving the menu closes it. Choosing an item closes the
 * menu and puts focus back on "More" first, so a drawer opened from the menu
 * returns focus there.
 */
function MoreMenu({ label, children }: { label: string; children: ReactNode }) {
  const ref = useRef<HTMLDetailsElement>(null);
  const close = (refocus: boolean) => {
    if (!ref.current?.open) return;
    ref.current.open = false;
    if (refocus) ref.current.querySelector('summary')?.focus();
  };
  return <details ref={ref} className="v96-more"
    onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); close(true); } }}
    onBlur={e => { if (!ref.current?.contains(e.relatedTarget as Node | null)) close(false); }}
    onClickCapture={e => { if ((e.target as HTMLElement).closest('.v96-more-list button')) close(true); }}>
    <summary aria-label={label}>More</summary>
    <div className="v96-more-list" role="group" aria-label={label}>{children}</div>
  </details>;
}

function ConnectorRow({ slug, connector: c, editable, busy, tracking, onPanel, onRun, onRefresh, onToggle, onGoTo = () => {}, hasKind = () => false, onAuditLog = () => {} }: {
  slug: string; connector: PostureConnector; editable: boolean; busy: string | null; tracking?: Tracking;
  onPanel: (p: Panel) => void; onRun: () => void; onRefresh: () => void; onToggle: () => void;
  /** Go to another connection of a kind (its row), or open its connect drawer when there is none. */
  onGoTo?: (kind: PostureConnectorKind, fromId?: string) => void; hasKind?: (kind: string) => boolean;
  /** Pinecone rows: open the AWS connection at its audit-log location field. */
  onAuditLog?: () => void;
}) {
  const collecting = tracking?.phase === 'running';
  const stalled = tracking?.phase === 'stalled';
  const rowRef = useRef<HTMLLIElement>(null);
  const refocus = useRef(false);
  // A run that never started (busy cleared, not collecting) drops the request, so a later run does not steal focus.
  useEffect(() => { if (!busy && !collecting) refocus.current = false; }, [busy, collecting]);
  useEffect(() => {
    if (!refocus.current || !collecting) return;
    refocus.current = false;
    rowRef.current?.querySelector<HTMLElement>('.v96-row-actions button, .v96-row-actions summary')?.focus();
  }, [collecting]);
  const health = connectorHealth(c, { collecting });
  const isUpload = c.kind === 'vuln_scanner';
  const isAws = c.kind === 'aws';
  // Google Cloud + Google Workspace can sign in keyless (workload identity) or with a key (weaker).
  const isWif = (c.kind === 'gcp' && c.auth_mode !== 'sa_key') || (c.kind === 'google_workspace' && c.auth_mode === 'wif');
  const isKey = (c.kind === 'gcp' || c.kind === 'google_workspace') && c.auth_mode === 'sa_key';
  const provider = PROVIDER_LABEL[c.kind];
  const name = c.display_name && c.display_name !== c.kind ? c.display_name : provider;
  const target = friendlyTarget(c);
  const isBusy = (k: string) => busy === `${k}:${c.connector_id}`;
  const guard = (fn: () => void) => () => { if (!busy) fn(); };
  const fix = () => onPanel({ mode: 'fix', connector: c });
  const isConsent = c.kind === 'azure_openai' && c.auth_mode === 'multitenant';
  const disconnected = isConsent && !!c.azure_disconnected;
  // A consent connection that has never run still needs its role assignments.
  const primaryIsSetup = isConsent && !disconnected && !c.last_run_at && !c.last_error_message && c.status !== 'disabled';
  const role = () => onPanel(isConsent ? { mode: 'azure', connector: c } : isWif ? { mode: 'gcp', connector: c } : { mode: 'role', connector: c });
  const upload = () => onPanel({ mode: 'upload', connector: c });
  const isPaused = c.status === 'disabled';
  const broken = health.key === 'error';

  // Exactly one visible action; the rest go under More.
  const pv = useContext(QuietRows) ? 'secondary' : 'primary';
  let primary: ReactNode = null;
  const reconnect = () => onPanel({ mode: 'connect', kind: 'azure_openai', prefill: c.form_values?.subscription_ids ?? '' });
  if (disconnected) primary = <Button variant={pv} onClick={reconnect}>Reconnect with Microsoft</Button>;
  else if (stalled) primary = <Button variant="secondary" onClick={onRefresh}>Refresh</Button>;
  else if (!broken) {
    if (isPaused) primary = <Button variant="secondary" aria-disabled={!!busy || undefined} onClick={guard(onToggle)}>{isBusy('toggle') ? 'Resuming…' : 'Resume'}</Button>;
    else if (isUpload) primary = <Button variant={c.last_run_at ? 'secondary' : pv} onClick={upload}>Upload scan</Button>;
    else if (primaryIsSetup) primary = <Button variant={pv} onClick={role}>View access setup</Button>;
    else primary = <Button variant="secondary" aria-disabled={!!busy || collecting || undefined} onClick={guard(() => { if (!collecting) onRun(); })}>{isBusy('run') ? 'Queuing…' : collecting ? 'Collecting…' : 'Collect now'}</Button>;
  }
  const more: ReactNode[] = [];
  if (isAws && !broken) more.push(<Button key="role" variant="ghost" onClick={role}>View role setup</Button>);
  if (isAws) more.push(<Button key="fix" variant="ghost" onClick={fix}>{broken ? 'Update role ARN or regions' : 'Edit regions or audit log'}</Button>);
  if (isWif && !broken) more.push(<Button key="role" variant="ghost" onClick={role}>View setup commands</Button>);
  if (isWif && broken) more.push(<Button key="fix" variant="ghost" onClick={fix}>Update connection details</Button>);
  if (isKey && !broken) more.push(<Button key="key" variant="ghost" onClick={() => onPanel({ mode: 'fix', connector: c, intent: 'replace-key' })}>Replace key</Button>);
  if (isConsent && !disconnected && !primaryIsSetup) more.push(<Button key="azure" variant="ghost" onClick={role}>View access setup</Button>);
  if (isConsent && !disconnected && !broken) more.push(<Button key="reconsent" variant="ghost" onClick={reconnect}>Reconnect with Microsoft</Button>);
  if (isConsent && !disconnected) more.push(<Button key="disconnect" variant="ghost" onClick={() => onPanel({ mode: 'disconnect', connector: c })}>Disconnect from Microsoft</Button>);
  // V112-2-6 (BL-25): a paused or disconnected connection can be removed for good (history kept).
  if (disconnected || isPaused) more.push(<Button key="remove" variant="ghost" onClick={() => onPanel({ mode: 'remove', connector: c })}>Remove connection</Button>);
  if (c.kind === 'pinecone') more.push(<Button key="audit" variant="ghost" onClick={onAuditLog}>{hasKind('aws') ? 'Audit log location (AWS)' : 'Connect AWS for audit logs'}</Button>);
  if (c.kind === 'openai' && !c.form_values?.chatgpt_workspace_id) more.push(<Button key="chatgpt" variant="ghost" onClick={() => onPanel({ mode: 'fix', connector: c, intent: 'add-chatgpt', focus: 'chatgpt_workspace_id' })}>Add ChatGPT Enterprise</Button>);
  if (!isUpload && !isWif && !isKey && !isConsent && !broken) more.push(<Button key="cred" variant="ghost" onClick={fix}>Edit connection</Button>);
  const due = c.privilege ? privilegeDue(c.privilege) : null;
  // V104-D-5: the collector says why (a reason code), so the button never depends on its wording.
  const needsDatadog = c.kind !== 'datadog' && !!c.reason_codes?.some(x => x === 'datadog_not_connected' || x === 'datadog_not_collected');
  // BL-17: a wrong audit-log bucket is reported on the AWS card; offer the field it lives in.
  const auditBtn = isAws && auditLogReason(c.last_error_message)
    ? <Button variant="secondary" onClick={() => onPanel({ mode: 'fix', connector: c, focus: 'pinecone_audit_s3' })}>Edit audit log location<span className="sr-only"> {name}</span></Button> : null;
  const reconfirm = () => onPanel({ mode: 'reconfirm', connector: c });
  const reconfirmBtn = c.privilege ? <Button variant="secondary" data-primary={health.key === 'reconfirm' || undefined} onClick={reconfirm}>Re-confirm<span className="sr-only"> admin {adminNoun(c.kind)}</span></Button> : null;
  // The label follows the stored status, so a paused connector that also errored offers Resume, not Pause.
  if (!disconnected && (broken || !isPaused)) more.push(<Button key="toggle" variant="ghost" aria-disabled={!!busy || undefined} onClick={guard(onToggle)}>{isPaused ? (isBusy('toggle') ? 'Resuming…' : 'Resume') : (isBusy('toggle') ? 'Pausing…' : 'Pause')}</Button>);

  return <li ref={rowRef} className="v96-source" id={`posture-${c.connector_id}`}>
    <div className="v96-source-head">
      <div className="v96-source-name">
        <strong>{name}</strong>
        {name !== provider && <small>{provider}</small>}
        {target && <small>{target}</small>}
        {!c.privilege && alwaysAdminKey(c.kind) && <span className="v101-priv"><Badge className="v75-status warning"><StatusGlyph kind="warning" /> {privilegeBadgeLabel(c.kind)}</Badge></span>}
        {c.privilege && due && <>
          <span className="v101-priv"><Badge className="v75-status warning"><StatusGlyph kind="warning" /> {privilegeBadgeLabel(c.kind)}</Badge></span>
          <small className={`v101-due${due.overdue || due.days <= RECONFIRM_SOON_DAYS ? ' overdue' : ''}`}><StatusGlyph kind={due.overdue ? 'warning' : 'clock'} /> {due.label}</small>
        </>}
        {isKey && <small className="v96-weaker"><StatusGlyph kind="warning" /> Key-based sign-in (weaker){editable && <> · <button type="button" className="v96-posture-link" onClick={() => onPanel({ mode: 'fix', connector: c, intent: 'switch' })}>{c.kind === 'gcp' ? 'Switch to workload identity' : 'Switch to keyless sign-in'}</button></>}</small>}
      </div>
      {disconnected
        ? <Badge className="v75-status neutral"><StatusGlyph kind="circle" /> Disconnected</Badge>
        : <Badge className={`v75-status ${stalled ? 'warning' : health.tone}`}><StatusGlyph kind={stalled ? 'clock' : health.glyph} /> {stalled ? 'Still collecting' : health.label}</Badge>}
    </div>
    <dl className="v96-facts">
      <div><dt>{isUpload ? 'Last scan' : broken ? 'Last attempt' : 'Last collected'}</dt><dd>{relativeTime(c.last_run_at)}</dd></div>
      <div><dt>Checks</dt><dd>{c.checks.pass + c.checks.fail === 0 ? ((c.checks.preview ?? 0) > 0 ? `${c.checks.preview} in preview` : 'None yet') : <>{c.checks.pass} passing{c.checks.fail > 0 ? <> · <Link className="v96-fail" to={`/w/${slug}/app/findings`}>{c.checks.fail} failing</Link></> : ' · 0 failing'}{(c.checks.preview ?? 0) > 0 ? ` · ${c.checks.preview} in preview` : ''}</>}</dd></div>
    </dl>
    {stalled && <p className="v75-muted v96-stalled">Still collecting — check back.</p>}
    {/* V104-D-4 (BL-21): a Datadog org without Cloud SIEM says so, instead of the detections check silently not appearing. */}
    {c.kind === 'datadog' && !broken && c.reason_codes?.includes('cloud_siem_not_in_use') && <div className="v96-note">
      <StatusGlyph kind="circle" />
      <div>
        <p>Cloud SIEM isn’t in use in this Datadog org, so security detections aren’t checked here. If another SIEM raises your alerts, connect it.</p>
        {editable && <div className="v96-error-actions">{(['splunk', 'sentinel'] as const).map(k => <Button key={k} variant="secondary" onClick={() => onGoTo(k)}>{hasKind(k) ? `Go to ${PROVIDER_LABEL[k]}` : `Connect ${PROVIDER_LABEL[k]}`}</Button>)}</div>}
      </div>
    </div>}
    {health.key === 'partial' && <div className="v96-partial"><StatusGlyph kind="warning" /> <div><ErrorText text={(() => { const p = partialRun(c.last_error_message)!; return [`${p.failed} of ${p.total} checks couldn't run.`, ...(c.last_error_message ?? '').split('\n').slice(1)].join('\n'); })()} />
      {/* Each reason names a next step; most happen in the connection's settings (V101-3-3 flow review). */}
      {editable && !isUpload && <div className="v96-error-actions">
        {auditBtn}
        {needsDatadog && <Button variant="secondary" onClick={() => onGoTo('datadog', c.connector_id)}>{hasKind('datadog') ? 'Go to Datadog' : 'Connect Datadog'}</Button>}
        <Button variant={needsDatadog ? 'ghost' : 'secondary'} onClick={isConsent ? role : fix}>{isConsent ? 'View access setup' : 'Edit connection'}<span className="sr-only"> {name}</span></Button>
      </div>}</div></div>}
    {broken && <div className="v96-error v96-error-stack">
      <div className="v96-error-text"><StatusGlyph kind="alert" /> <div><ErrorText text={c.last_error_message} /></div></div>
      {editable && <div className="v96-error-actions">{auditBtn}{(isUpload
        ? <Button data-primary variant={pv} onClick={upload}>Upload scan</Button>
        : isAws ? <Button data-primary variant={pv} onClick={role}>View role setup</Button>
        : isWif ? <Button data-primary variant={pv} onClick={role}>View setup commands</Button>
        // V112-2-7 (BL-25): the run's reason picks the one fix: revoked / expired consent → reconnect; a missing
        // role → access setup. Unknown keeps both.
        : isConsent ? (c.run_reason === 'credential_expired' || c.run_reason === 'not_connected'
            ? <Button data-primary variant={pv} onClick={reconnect}>Reconnect with Microsoft<span className="sr-only"> {name}</span></Button>
            : c.run_reason === 'access_denied'
              ? <Button data-primary variant={pv} onClick={role}>View access setup</Button>
              : <><Button data-primary variant={pv} onClick={role}>View access setup</Button><Button variant="secondary" onClick={reconnect}>Reconnect with Microsoft<span className="sr-only"> {name}</span></Button></>)
        : <Button data-primary variant={pv} onClick={fix}>Fix connection<span className="sr-only"> for {name}</span></Button>)}
      {/* Many fixes happen in the vendor's console (e.g. a key's scopes): collect again without re-entering anything. */}
      {!isUpload && <Button variant="secondary" aria-disabled={!!busy || undefined} onClick={guard(() => { refocus.current = true; onRun(); })}>{isBusy('run') ? 'Queuing…' : 'Collect now'}<span className="sr-only"> {name}</span></Button>}
      </div>}
    </div>}
    {editable && (primary || reconfirmBtn || more.length > 0) && <div className="v75-actions v96-row-actions">
      {primary}
      {reconfirmBtn}
      {more.length > 0 && <MoreMenu label={`More actions for ${name}`}>{more}</MoreMenu>}
    </div>}
    {/* ADR-0102 §6: a grant from before the metric scopes. Info, never an error: config checks keep running. */}
    {c.regrant && !broken && !isPaused && <Alert variant="info" title="New permission for monitoring" className="v113-regrant">
      <p>Adds read-only usage and guardrail metrics. Config checks keep running.</p>
      {regrantChips(c.regrant.scopes)}
      {editable
        ? <div className="v75-actions"><Button variant="secondary" onClick={() => onPanel({ mode: 'regrant', connector: c })}>Update permissions<span className="sr-only"> for {name}</span></Button></div>
        : <p className="v75-muted">An owner or admin can update this.</p>}
    </Alert>}
    {(c.scopes.length > 0 || c.target) && <details className="v96-scopes">
      <summary>Access granted{c.scopes.length ? ` (${c.scopes.length})` : ''}</summary>
      {c.target && <p className="v96-target">{c.target}</p>}
      {c.scopes.length > 0 && <ul>{c.scopes.map(s => <li key={s}><code>{s}</code></li>)}</ul>}
    </details>}
  </li>;
}

// ---- drawer ----------------------------------------------------------------------

function PosturePanel({ slug, panel, onClose, onPanel, onDone, onPageMessage, track, returnFocus, live, say, back }: {
  slug: string; panel: Panel; onClose: () => void; onPanel: (p: Panel) => void; onDone: (message: string) => void;
  /** A result that outlives the drawer (shown and announced on the page after it closes). */
  onPageMessage: (message: string) => void;
  track: TrackApi; returnFocus: () => HTMLElement | null; live: string; say: (m: string) => void; back: { to: string; label: string } | null;
}) {
  const kind = panel.mode === 'connect' ? panel.kind : panel.connector.kind;
  // BL-17: a healthy (or only partly failing) row is edited, not fixed; a failed check or an error row is fixed.
  const editNotFix = panel.mode === 'fix' && connectorHealth(panel.connector).key !== 'error' && track.tracking[panel.connector.connector_id]?.phase !== 'error';
  const title = panel.mode === 'connect' ? (panel.intent === 'add-chatgpt' ? 'Connect OpenAI with ChatGPT Enterprise' : `Connect ${PROVIDER_LABEL[kind]}`)
    : panel.mode === 'fix' ? (panel.intent === 'switch' ? (kind === 'gcp' ? 'Switch Google Cloud to workload identity' : `Switch ${PROVIDER_LABEL[kind]} to keyless sign-in`) : panel.intent === 'replace-key' ? `Replace ${PROVIDER_LABEL[kind]} key` : panel.intent === 'add-chatgpt' ? 'Add ChatGPT Enterprise' : `${editNotFix ? 'Edit' : 'Fix'} ${PROVIDER_LABEL[kind]} connection`)
    : panel.mode === 'check' ? `Check ${PROVIDER_LABEL[kind]} connection`
    : panel.mode === 'role' ? 'AWS role setup'
    : panel.mode === 'azure' ? 'Azure access setup'
    : panel.mode === 'disconnect' ? 'Disconnect Azure OpenAI'
    : panel.mode === 'remove' ? `Remove ${PROVIDER_LABEL[kind]} connection`
    : panel.mode === 'gcp' ? `${PROVIDER_LABEL[kind]} setup`
    : panel.mode === 'regrant' ? `Update ${PROVIDER_LABEL[kind]} permissions`
    : panel.mode === 'reconfirm' ? (adminNoun(kind) === 'key' ? 'Re-confirm admin key' : 'Re-confirm admin access') : 'Upload scan';
  const heading = useRef<HTMLHeadingElement>(null);
  // Each step change moves focus to the drawer heading; the first open leaves the Drawer's first-field focus alone.
  // Compared against the last mode (not a first-run flag) so StrictMode's double effect run is a no-op.
  const stepKey = `${panel.mode}:${kind}`;
  const lastMode = useRef(stepKey);
  useEffect(() => { if (lastMode.current === stepKey) return; lastMode.current = stepKey; heading.current?.focus(); }, [stepKey]);

  const startCheck = async (c: PostureConnector) => {
    try { await track.collect(c); } catch (e) { say(friendlyRunError(e)); }
  };

  return <Drawer open modal onClose={onClose} label={title} className="v96-drawer" returnFocus={returnFocus}>
    <div className="v96-drawer-head">
      <h2 ref={heading} tabIndex={-1}>{title}</h2>
      <Button variant="ghost" onClick={onClose} aria-label="Close">✕</Button>
    </div>
    <div role="status" aria-live="polite" className="sr-only">{live}</div>
    {panel.mode === 'connect' || panel.mode === 'fix'
      ? <CredentialForm slug={slug} kind={kind} connector={panel.mode === 'fix' ? panel.connector : undefined} intent={panel.mode === 'fix' || panel.mode === 'connect' ? panel.intent : undefined} onSwitchKind={k => onPanel({ mode: 'connect', kind: k })} focus={panel.mode === 'connect' || panel.mode === 'fix' ? panel.focus : undefined} consent={panel.mode === 'connect' ? panel.consent : undefined} consentWorkspace={panel.mode === 'connect' ? panel.consentWorkspace : undefined} prefill={panel.mode === 'connect' ? panel.prefill : undefined} onClose={onClose}
          onSaved={(connector, template, wif, retireKey, roleChanged) => {
            if ((kind === 'gcp' || kind === 'google_workspace') && wif) { onDone(`${PROVIDER_LABEL[kind]} connection saved.`); say(`${PROVIDER_LABEL[kind]} connection saved. Run the setup commands, then check the connection.`); onPanel({ mode: 'gcp', connector, template: wif, retireKey }); }
            else if (kind === 'aws' && panel.mode === 'fix' && !roleChanged && connectorHealth(panel.connector, { collecting: false }).key !== 'error') {
              onDone('AWS settings saved.'); say('AWS settings saved. Checking the connection.');
              onPanel({ mode: 'check', connector });
              void startCheck(connector);
            }
            else if (kind === 'aws') { onDone('AWS role saved.'); say('AWS role saved. Apply the role setup, then check the connection.'); onPanel({ mode: 'role', connector, template }); }
            else if (kind === 'vuln_scanner') { onDone('Scan upload ready.'); say('Scan upload ready.'); onPanel({ mode: 'upload', connector }); }
            else {
              const m = `${PROVIDER_LABEL[kind]} ${panel.mode === 'fix' ? 'credentials updated' : 'connected'}.`;
              onDone(m); say(`${m} Checking the connection.`);
              onPanel({ mode: 'check', connector });
              void startCheck(connector);
            }
          }} />
      : panel.mode === 'check' ? <div className="v96-form">
          <CheckSection connector={panel.connector} tracking={track.tracking[panel.connector.connector_id]}
            onCheck={() => void startCheck(panel.connector)} onRepoll={() => track.repoll(panel.connector)}
            onFix={() => onPanel(panel.connector.kind === 'aws' ? { mode: 'role', connector: panel.connector }
              : panel.connector.kind === 'gcp' && panel.connector.auth_mode !== 'sa_key' ? { mode: 'gcp', connector: panel.connector }
              : { mode: 'fix', connector: panel.connector })}
            onClose={onClose} />
          {back && <Link className="v101-back" to={back.to}><span aria-hidden="true">← </span>{`Back to ${back.label}`}</Link>}
        </div>
      : panel.mode === 'regrant' ? <RegrantSetup slug={slug} connector={panel.connector} tracking={track.tracking[panel.connector.connector_id]} onClose={onClose}
          onFullSetup={() => onPanel(panel.connector.kind === 'aws' ? { mode: 'role', connector: panel.connector } : { mode: 'gcp', connector: panel.connector })}
          onNewToken={() => onPanel({ mode: 'fix', connector: panel.connector })}
          check={<CheckSection connector={panel.connector} tracking={track.tracking[panel.connector.connector_id]} emphasis="primary" ownResult
            onCheck={() => void startCheck(panel.connector)} onRepoll={() => track.repoll(panel.connector)}
            onFix={() => onPanel({ mode: 'fix', connector: panel.connector })} onClose={onClose} />} />
      : panel.mode === 'reconfirm' ? <ReconfirmPrivilege slug={slug} connector={panel.connector} onClose={onClose} onDone={m => { onDone(m); say(m); }} />
      : panel.mode === 'role' ? <RoleSetup slug={slug} connector={panel.connector} initial={panel.template} onClose={onClose}
          check={<CheckSection connector={panel.connector} tracking={track.tracking[panel.connector.connector_id]} emphasis="primary"
            onCheck={() => void startCheck(panel.connector)} onRepoll={() => track.repoll(panel.connector)}
            fixLabel="Update role ARN or regions"
            onFix={() => onPanel({ mode: 'fix', connector: panel.connector })} onClose={onClose} />} />
      : panel.mode === 'disconnect' ? <AzureDisconnect slug={slug} connector={panel.connector} onClose={onClose} onDone={m => { onClose(); onDone(m); onPageMessage(m); }} />
      : panel.mode === 'remove' ? <RemoveConnection slug={slug} connector={panel.connector} onClose={onClose} onDone={m => { onClose(); onDone(m); onPageMessage(m); }} />
      : panel.mode === 'azure' ? <AzureAccessSetup connector={panel.connector}
          check={showSteps => <CheckSection connector={panel.connector} tracking={track.tracking[panel.connector.connector_id]} emphasis="primary"
            onCheck={() => void startCheck(panel.connector)} onRepoll={() => track.repoll(panel.connector)}
            fixLabel="Show role commands" onFix={showSteps} onClose={onClose} />} />
      : panel.mode === 'gcp' ? <GcpSetup slug={slug} connector={panel.connector} initial={panel.template} retireKey={panel.retireKey} onClose={onClose}
          check={emphasis => <CheckSection connector={panel.connector} tracking={track.tracking[panel.connector.connector_id]} emphasis={emphasis}
            onCheck={() => void startCheck(panel.connector)} onRepoll={() => track.repoll(panel.connector)}
            fixLabel="Update connection details"
            onFix={() => onPanel({ mode: 'fix', connector: panel.connector })} onClose={onClose} />} />
      : <ScanUpload slug={slug} connector={panel.connector} say={say} onUploaded={onDone} onQueued={() => track.watch(panel.connector)} onClose={onClose} />}
  </Drawer>;
}

function placeholderConnector(id: string, kind: PostureConnectorKind, target: string | null, authMode?: 'wif' | 'sa_key'): PostureConnector {
  return { connector_id: id, kind, status: 'active', display_name: PROVIDER_LABEL[kind], target, auth_mode: authMode ?? null, scopes: [], configured_at: new Date().toISOString(), last_run_at: null, last_error_at: null, last_error_message: null, checks: { pass: 0, fail: 0, last_observed_at: null } };
}

/**
 * A collector error: first line is the plain summary; any further lines
 * (distinct reasons, one per line) sit under a Details disclosure.
 */
/** Text with a line-break point after each underscore, so identifiers such as scope names wrap cleanly. */
export function breakable(text: string): ReactNode {
  if (!text.includes('_')) return text;
  return text.split('_').map((w, j) => <Fragment key={j}>{j > 0 && <>_<wbr /></>}{w}</Fragment>);
}

/** Collector messages mark scope names with backticks; they read as the same code chips as the connect form's hint. */
export function withChips(text: string): ReactNode {
  return text.split(/`([^`]+)`/).map((seg, i) => i % 2 === 1 ? <code key={i} className="v101-chip">{breakable(seg)}</code> : <Fragment key={i}>{breakable(seg)}</Fragment>);
}
/** The same message for a screen reader announcement or plain text (no markers). */
const plainText = (text: string) => text.replace(/`/g, '');

export function ErrorText({ text }: { text: string | null | undefined }) {
  const [summary, ...reasons] = (text ?? '').split('\n').map(l => l.trim()).filter(Boolean);
  if (!summary) return <>The last collection failed.</>;
  return <>
    {withChips(summary)}
    {reasons.length > 0 && <details className="v96-scopes v96-error-details">
      <summary>Details</summary>
      <ul>{reasons.map(r => <li key={r}>{withChips(r)}</li>)}</ul>
    </details>}
  </>;
}

/** Google Workspace setup checklist shown when a check fails: the usual three fixes + the exact delegation scopes to copy. */
function GoogleWorkspaceFix({ scopes }: { scopes: string[] }) {
  const [copied, setCopied] = useState('');
  const list = scopes.join(',');
  const copy = async () => {
    setCopied('');
    try { await navigator.clipboard.writeText(list); window.setTimeout(() => setCopied('Scopes copied.'), 50); }
    catch { window.setTimeout(() => setCopied('Copy was unavailable. Open “Show scopes” and copy the text.'), 50); }
  };
  return <div className="v96-fix">
    <p><strong>How to fix</strong></p>
    <ol className="v96-steps">
      <li>In Google Cloud, turn on the <strong>Admin SDK API</strong> and <strong>Cloud Identity API</strong> for the service account’s project.</li>
      <li>In Google Admin, make sure the admin email you entered is a <strong>super admin</strong>.</li>
      <li>In <a href="https://admin.google.com/ac/owl/domainwidedelegation" target="_blank" rel="noreferrer">Domain-wide delegation</a>, make sure the service account’s client ID is listed with these scopes.</li>
    </ol>
    {scopes.length > 0 && <>
      <div className="v75-actions"><Button variant="secondary" onClick={() => void copy()}>Copy scopes</Button><span role="status" className="v75-muted">{copied}</span></div>
      <details className="v96-scopes"><summary>Show scopes</summary><pre className="v96-code" tabIndex={0} role="region" aria-label="Domain-wide delegation scopes">{list}</pre></details>
    </>}
    <p className="v75-muted">Changes can take a few minutes to apply. Then check again.</p>
  </div>;
}

/**
 * The connection check, driven by the panel's tracking (so it keeps running
 * if the drawer closes). Idle → Check connection; running → Checking…;
 * ok → result; error → Check again + Fix (AWS: View role setup);
 * stalled → Check again re-polls the run already in flight.
 */
export function CheckSection({ connector, tracking, onCheck, onRepoll, onFix, onClose, emphasis = 'primary', fixLabel, ownResult = false }: {
  connector: PostureConnector; tracking?: Tracking; onCheck: () => void; onRepoll: () => void; onFix: () => void; onClose: () => void;
  emphasis?: 'primary' | 'secondary'; fixLabel?: string;
  /** The caller states the outcome of a finished run itself (re-grant: did the new permission land), so only one answer shows. */
  ownResult?: boolean;
}) {
  const phase = tracking?.phase ?? 'idle';
  const aws = connector.kind === 'aws';
  // One button stays mounted through every phase (label + aria-disabled change), so focus is never dropped.
  const label = phase === 'idle' ? 'Check connection' : phase === 'running' ? 'Checking…' : 'Check again';
  const onPress = () => { if (phase === 'running') return; if (phase === 'stalled') onRepoll(); else onCheck(); };
  return <div className="v96-check">
    <div className="v96-check-result">
      {phase === 'running' && <p className="v96-checking"><StatusGlyph kind="dots" /> Checking — collection is running.</p>}
      {phase === 'ok' && !ownResult && <Alert variant="success" title="Connection works">{tracking?.message}</Alert>}
      {phase === 'error' && <Alert variant="error" title="Connection failed"><div><ErrorText text={tracking?.message} /></div></Alert>}
      {phase === 'partial' && !ownResult && (() => {
        const p = partialRun(tracking?.message)!;
        const [, ...reasons] = (tracking?.message ?? '').split('\n');
        return <Alert variant="warning" title="Connected">
          <div><ErrorText text={[`${p.total - p.failed} of ${p.total} checks collected; ${p.failed} couldn't run.`, ...reasons].join('\n')} /></div>
        </Alert>;
      })()}
      {phase === 'error' && connector.kind === 'google_workspace' && connector.auth_mode !== 'wif' && <GoogleWorkspaceFix scopes={connector.scopes} />}
      {phase === 'stalled' && <Alert variant="info" title="Still collecting">Check back, or check again.</Alert>}
    </div>
    <div className="v75-actions">
      <Button variant={phase === 'error' || phase === 'stalled' ? 'primary' : phase === 'ok' || phase === 'partial' ? 'secondary' : emphasis} aria-disabled={phase === 'running' || undefined} onClick={onPress}>{label}</Button>
      {phase === 'error' && <Button variant="secondary" onClick={onFix}>{fixLabel ?? (aws ? 'View role setup' : 'Fix connection')}</Button>}
      <Button variant="ghost" onClick={onClose}>{phase === 'ok' || phase === 'partial' ? 'Done' : 'Close'}</Button>
    </div>
  </div>;
}

function CredentialForm({ slug, kind, connector, intent, consent, consentWorkspace, prefill, focus, onSwitchKind, onClose, onSaved }: {
  /** V112-2-8: the caller's own workspace that holds the tenant (tenant_in_use only). */
  consentWorkspace?: { slug: string; name: string };
  slug: string; kind: PostureConnectorKind; connector?: PostureConnector; intent?: 'switch' | 'replace-key' | 'add-chatgpt'; onClose: () => void;
  onSaved: (connector: PostureConnector, template?: AwsRoleTemplate, wif?: GcpWifTemplate, retireKey?: string[], roleChanged?: boolean) => void;
  /** ADR-0062: the result Microsoft's consent returned with (shown on the Azure OpenAI form). */
  consent?: string;
  /** ADR-0062: subscription IDs to start the consent form with (reconnect). */
  prefill?: string;
  /** Field key to focus once the drawer is open (e.g. the AWS audit-log location). */
  focus?: string;
  /** Switch the drawer to another kind's connect form (e.g. Datadog, from the ChatGPT log index hint). */
  onSwitchKind?: (kind: PostureConnectorKind) => void;
}) {
  // GCP: the sign-in method follows the intent, then the connection's current method.
  const initialAuth = intent === 'switch' ? 'wif' : intent === 'replace-key' ? 'sa_key' : connector?.auth_mode ?? 'wif';
  const manifestForm = manifestFormOf(kind);
  // A manifest select starts on its first option (e.g. the Datadog site).
  const selects = Object.fromEntries((manifestForm?.fields ?? []).filter(f => f.options?.length).map(f => [f.key, f.options![0]!.value]));
  const initial: Record<string, string> = { auth_scheme: 'SSWS', auth: initialAuth, ...selects };
  // Every non-secret manifest field comes back pre-filled (Azure subscriptions, Datadog site…).
  if (connector?.form_values) Object.assign(initial, connector.form_values);
  if (connector?.target) {
    if (kind === 'okta_posture' || kind === 'okta') initial.org_url = connector.target;
    if (kind === 'aws') initial.role_arn = connector.target;
    if (kind === 'entra_posture' || kind === 'entra' || kind === 'microsoft_365') initial.tenant_id = connector.target;
    if (kind === 'gcp') initial.scope = connector.target;
    // G-1h: a manifest-form kind pre-fills the field its list target comes from.
    const m = getConnectorManifest(kind);
    const tf = m && manifestForm ? connectorTargetField(m) : null;
    if (tf && manifestForm?.fields.some(f => f.key === tf && f.store !== 'credential')) initial[tf] = connector.target;
  }
  if (connector?.host_project_id) initial.host_project_id = connector.host_project_id;
  if (connector?.host_project_number) initial.host_project_number = connector.host_project_number;
  if (kind === 'google_workspace' && connector?.admin_email) initial.subject = connector.admin_email;
  if (kind === 'aws' && connector?.regions?.length) initial.regions = connector.regions.join(', ');
  // V101-3-6: the AWS form offers the Pinecone audit-log location only to workspaces that use Pinecone.
  const queryClient = useQueryClient();
  if (kind === 'aws' && queryClient.getQueryData<{ connectors: PostureConnector[] }>(queryKeys.postureConnectors(slug))?.connectors.some(c => c.kind === 'pinecone')) initial._has_pinecone = '1';
  const [values, setValues] = useState<Record<string, string>>(initial);
  const [azureMode, setAzureMode] = useState<'consent' | 'app'>('consent');
  // BL-18: one set of subscription IDs for both ways of connecting Azure OpenAI.
  const [consentSubs, setConsentSubs] = useState<string | undefined>(prefill);
  const [backToConsent, setBackToConsent] = useState(false);
  const switchedToApp = useRef(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // V101-3-3: 'Add ChatGPT Enterprise' opens the drawer with that optional section open.
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set([
    ...(intent === 'add-chatgpt' ? ['chatgpt_enterprise'] : []),
    ...(getConnectorManifest(kind)?.form?.fields ?? []).filter((f) => f.group?.collapsed && connector?.form_values?.[f.key]).map((f) => f.group!.id),
  ]));
  const [formErrors, setFormErrors] = useState<string[]>([]);
  // BL-17: a wrong audit-log bucket is shown next to its field until the field is edited.
  const [auditNote, setAuditNote] = useState<string | null>(() => (kind === 'aws' && connector ? auditLogReason(connector.last_error_message) : null));
  const [submitting, setSubmitting] = useState(false);
  const [wifUnavailable, setWifUnavailable] = useState(false);
  // ADR-0053 §2: an admin-key kind is confirmed before submit (manifest), or after a 422 (server copy).
  const [serverPrivilege, setServerPrivilege] = useState<PrivilegeDeclaration | null>(null);
  // V101-7-2 (L-5): replacing an admin key is a new declaration too, so the Fix /
  // Update-credentials form asks for the same confirmation as Connect.
  const declared: PrivilegeDeclaration | null = getConnectorManifest(kind)?.privilege ?? serverPrivilege;
  // D-CI-34: an optional admin credential asks for confirmation only once it is filled in.
  const privilege: PrivilegeDeclaration | null = declared && privilegeApplies({ privilege: declared }, values) ? declared : null;
  const [privilegeOk, setPrivilegeOk] = useState(false);
  const [privilegeError, setPrivilegeError] = useState<string | undefined>();
  const privilegeBox = useRef<HTMLInputElement>(null);
  const fallbackBtn = useRef<HTMLDivElement>(null);
  // Recovery is at the top of the form: take focus there so keyboard users are not left on Connect.
  useEffect(() => { if (wifUnavailable) fallbackBtn.current?.querySelector('button')?.focus(); }, [wifUnavailable]);
  const [formLive, setFormLive] = useState('');
  const form = useRef<HTMLFormElement>(null);
  // The drawer focuses its first field on open; a caller that names a field takes focus after it.
  useEffect(() => {
    if (!focus) return;
    const target = () => form.current?.querySelector<HTMLElement>(`#posture-${kind}-${focus}`);
    const go = () => { const el = target(); if (el && document.activeElement !== el) { el.scrollIntoView?.({ block: 'center' }); el.focus(); } };
    // The Drawer focuses its own first field on open; take focus after it, and once more if it won the race.
    const t1 = window.setTimeout(go, 80);
    const t2 = window.setTimeout(go, 300);
    return () => { window.clearTimeout(t1); window.clearTimeout(t2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // ADR-0062: after switching to the own-app form, focus its first field and say so.
  useEffect(() => {
    if (azureMode !== 'app' || switchedToApp.current) return;
    switchedToApp.current = true;
    form.current?.querySelector<HTMLInputElement>('input')?.focus();
    setFormLive('Showing the form for your own app registration.');
  }, [azureMode]);
  const keptStored = keptOnUpdate(kind, connector);
  const together = getConnectorManifest(kind)?.form?.together ?? [];
  const inPair = (k: string) => together.some((set) => set.includes(k));
  // A required key always stays when blank; a paired one only while its partner is filled.
  const kept = new Set([...keptStored].filter((k) => !inPair(k) || together.some((set) => set.includes(k) && set.some((o) => o !== k && !keptStored.has(o) && (values[o] ?? '').trim()))));
  // D-CI-34: announce the admin confirmation when it appears; clearing the credential resets the tick.
  const hadPrivilege = useRef(!!privilege);
  useEffect(() => {
    if (!!privilege === hadPrivilege.current) return;
    hadPrivilege.current = !!privilege;
    if (privilege) setFormLive('Admin access confirmation added below the form fields.');
    else { setPrivilegeOk(false); setPrivilegeError(undefined); }
  }, [privilege]);
  const pairOf = (k: string) => getConnectorManifest(kind)?.form?.together?.find((s) => s.includes(k)) ?? [k];
  const set = (k: string, v: string) => {
    setValues(prev => ({ ...prev, [k]: v }));
    if (k === 'pinecone_audit_s3') setAuditNote(null);
    const clear = pairOf(k).filter((x) => errors[x]);
    if (clear.length) setErrors(prev => { const n = { ...prev }; for (const x of clear) delete n[x]; return n; });
  };
  /** Switching the GCP sign-in method drops any loaded key (never submit a key the user moved away from) and says what changed. */
  const setAuth = (v: string) => {
    setValues(prev => ({ ...prev, auth: v, client_email: '', private_key: '', key_id: '' }));
    setErrors({});
    setFormLive('');
    const keyless = kind === 'gcp' ? 'workload identity' : 'keyless sign-in';
    window.setTimeout(() => setFormLive(v === 'sa_key'
      ? `Service account key selected. Key file field shown. Keys are long-lived secrets; ${keyless} is safer.`
      : `${keyless[0]!.toUpperCase()}${keyless.slice(1)} selected. Host project fields shown.`), 50);
  };

  // Focus moves to the first error only after a submit: clearing one field's error while the user
  // types must never move focus (V101-3-3 a11y review: keystrokes landed in another secret field).
  const focusErrors = useRef(false);
  /** Submit-time errors: open every collapsed section holding one (same render), then focus the first. */
  const showErrors = (errs: Record<string, string>, extra = 0) => {
    const sections = fields.filter((f) => errs[f.key] && f.group?.collapsed).map((f) => f.group!.id);
    if (sections.length) setOpenGroups((prev) => new Set([...prev, ...sections]));
    focusErrors.current = true;
    setErrors(errs);
    // V104-D-5: one summary instead of one alert per field; focus moves to the first invalid field, which reads its own error.
    const n = Object.keys(errs).length + extra;
    const first = fields.find((f) => errs[f.key]);
    setFormLive('');
    window.setTimeout(() => setFormLive(`${n} ${n === 1 ? 'problem' : 'problems'} to fix${first ? `, starting with ${first.label}` : ''}.`), 50);
  };
  useEffect(() => {
    if (!focusErrors.current || Object.keys(errors).length === 0) return;
    focusErrors.current = false;
    // Every optional section holding an error opens, so no error is left hidden.
    form.current?.querySelectorAll<HTMLElement>('[aria-invalid="true"]').forEach((f) => { const d = f.closest('details'); if (d && !d.open) d.open = true; });
    // The first field with an error, in form order: the same one the summary names.
    const firstKey = fields.find((f) => errors[f.key])?.key;
    ((firstKey ? form.current?.querySelector<HTMLElement>(`#posture-${kind}-${firstKey}`) : null) ?? form.current?.querySelector<HTMLElement>('[aria-invalid="true"]'))?.focus();
  }, [errors]);

  const readKeyFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      const json = JSON.parse(await file.text()) as { client_email?: unknown; private_key?: unknown };
      if (typeof json.client_email !== 'string' || typeof json.private_key !== 'string') throw new Error('missing fields');
      const keyId = typeof (json as { private_key_id?: unknown }).private_key_id === 'string' ? (json as { private_key_id: string }).private_key_id : '';
      setValues(prev => ({ ...prev, client_email: json.client_email as string, private_key: json.private_key as string, key_id: keyId }));
      setErrors(prev => { const n = { ...prev }; delete n.service_account; return n; });
      setFormLive(''); window.setTimeout(() => setFormLive(`Key file loaded for ${json.client_email as string}.`), 50);
    } catch {
      setValues(prev => ({ ...prev, client_email: '', private_key: '' }));
      setErrors(prev => ({ ...prev, service_account: 'This is not a service account key file. Download a JSON key for the service account.' }));
      setFormLive(''); window.setTimeout(() => setFormLive('This is not a service account key file. Download a JSON key for the service account.'), 50);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setFormErrors([]);
    const local = validatePostureForm(kind, values, connector ? kept : undefined);
    const privilegeMissing = !!privilege && !privilegeOk;
    // Every error in one pass: field errors and the admin-key confirmation together.
    if (privilegeMissing) setPrivilegeError(PRIVILEGE_REQUIRED);
    if (Object.keys(local).length) { showErrors(local, privilegeMissing ? 1 : 0); return; }
    if (privilegeMissing) {
      privilegeBox.current?.focus();
      setFormLive(''); window.setTimeout(() => setFormLive('1 problem to fix: confirm the admin access.'), 50);
      return;
    }
    setSubmitting(true);
    try {
      const body = toBody(kind, values);
      if (privilege && privilegeOk) body.confirm_privilege = true;
      if (connector) {
        const r = await updatePostureCredentials(slug, connector.connector_id, body);
        // The server clears the error and reactivates on a credential update.
        const authMode = kind === 'gcp' || kind === 'google_workspace' ? (isKeyAuth(values) ? 'sa_key' : 'wif') : connector.auth_mode;
        onSaved({ ...connector, status: 'active', last_error_message: null, auth_mode: authMode }, r.role_template, r.wif_template, r.retire_key_commands,
          kind === 'aws' && (values.role_arn ?? '').trim() !== (connector.target ?? ''));
      } else {
        const r = await createPostureConnector(slug, kind, body);
        const target = kind === 'aws' ? values.role_arn.trim() : kind === 'gcp' ? values.scope.trim() : null;
        onSaved({ ...placeholderConnector(r.connector_id, kind, target, kind === 'gcp' || kind === 'google_workspace' ? (isKeyAuth(values) ? 'sa_key' : 'wif') : undefined), scopes: r.scopes ?? [] }, r.role_template, r.wif_template);
      }
    } catch (err) {
      // Workload identity is off in this environment: offer the key path instead of a dead end.
      if ((kind === 'gcp' || kind === 'google_workspace') && !isKeyAuth(values) && err instanceof ApiError && err.status === 503) { setWifUnavailable(true); return; }
      const apiBody = err instanceof ApiError ? err.body as { errors?: PostureFieldError[]; error?: string; privilege?: PrivilegeDeclaration } | undefined : undefined;
      if (err instanceof ApiError && err.status === 422 && apiBody?.error === 'privilege_confirmation_required') {
        if (apiBody.privilege) setServerPrivilege(apiBody.privilege);
        setPrivilegeOk(false);
        setPrivilegeError(PRIVILEGE_REQUIRED);
        window.setTimeout(() => privilegeBox.current?.focus(), 0);
        return;
      }
      const { fields, rest } = splitServerErrors(kind, apiBody?.errors);
      if (Object.keys(fields).length) showErrors(fields);
      if (rest.length) setFormErrors(rest);
      else if (!Object.keys(fields).length) setFormErrors([err instanceof Error ? err.message : 'The connection could not be saved. Please retry.']);
    } finally { setSubmitting(false); }
  };

  const fields = fieldsFor(kind, values);
  // ADR-0062: a new Azure OpenAI connection starts with Microsoft admin consent; the own-app form is the fallback.
  if (kind === 'azure_openai' && !connector && azureMode === 'consent') {
    return <AzureConsentStart slug={slug} consent={consent} consentWorkspace={consentWorkspace} prefill={consentSubs} returned={backToConsent}
      onUseOwnApp={(subs) => { if (subs.trim() && !(values.subscription_ids ?? '').trim()) setValues(prev => ({ ...prev, subscription_ids: subs.trim() })); setAzureMode('app'); }} onClose={onClose} />;
  }
  // V104-D-5 (BL-22): the log index hint says whether Datadog is connected and what this workspace's profile requires.
  const listed = queryClient.getQueryData<{ connectors: PostureConnector[]; log_retention_target_days?: number }>(queryKeys.postureConnectors(slug));
  const hasDatadog = !!listed?.connectors.some(c => c.kind === 'datadog');
  const target = listed?.log_retention_target_days;
  const keepFor = target ? `The index must keep logs ${target} days or more.` : 'The index must keep logs for your required retention.';
  // Switching the drawer to Datadog drops what was typed here, so the link is offered only while nothing has been changed.
  const dirty = Object.keys(values).some(k => (values[k] ?? '') !== (initial[k] ?? ''));
  const indexHint: ReactNode = hasDatadog
    ? `Checked against your Datadog connection. ${keepFor}`
    : `Needs a Datadog connection${onSwitchKind && !dirty ? '' : ' (connect it after saving this form)'}. ${keepFor}`;
  const connectDatadog = kind === 'openai' && !hasDatadog && !!onSwitchKind && !dirty
    ? <div><button type="button" className="v96-posture-link" onClick={() => onSwitchKind('datadog')}>Connect Datadog</button></div> : null;
  const renderField = (f: FieldSpec) => {
    const id = `posture-${kind}-${f.key}`;
    if (f.kind === 'key-file') {
      return <Field key={f.key} id={id} label={f.label} required error={errors[f.key]} errorRole="none" hint={values.client_email ? `Loaded: ${values.client_email}` : 'From Google Cloud → IAM → Service accounts → Keys.'}>
        <Input key={`${f.key}-${values.auth ?? ''}`} type="file" accept="application/json,.json" onChange={e => void readKeyFile(e.currentTarget.files?.[0])} />
      </Field>;
    }
    if (f.kind === 'select') {
      const keyWarning = f.key === 'auth' && isKeyAuth(values);
      return <Field key={f.key} id={id} label={f.label} required={f.required} error={errors[f.key]} errorRole="none"
        hint={keyWarning ? <span className="v96-weaker"><StatusGlyph kind="warning" /> Keys are long-lived secrets. {kind === 'gcp' ? 'Workload identity' : 'Keyless sign-in'} is safer.</span> : typeof f.hint === 'function' ? f.hint(values) : f.hint}>
        <Select value={values[f.key] ?? ''} onChange={e => (f.key === 'auth' ? setAuth : (v: string) => set(f.key, v))(e.currentTarget.value)}>{f.options!.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</Select>
      </Field>;
    }
    // V101-3-6: a paired secret left blank on update keeps the stored one.
    const keptHint = connector && kept.has(f.key) ? 'Leave blank to keep the stored value.' : null;
    const hint = kind === 'openai' && f.key === 'chatgpt_log_index' ? indexHint : typeof f.hint === 'function' ? f.hint(values) : f.hint;
    // BL-17: required fields carry the asterisk; an optional field outside an optional group says so in its label.
    const label = !f.required && !f.group && !/\(optional\)/i.test(f.label) ? `${f.label} (optional)` : f.label;
    const error = errors[f.key] ?? (f.key === 'pinecone_audit_s3' ? auditNote ?? undefined : undefined);
    // V104-D-4: the error above the form is about this key; point the field at it until the key is re-entered.
    const aboutKey = !!connector?.last_error_message && FIX_ERROR_FIELD[kind] === f.key && !(values[f.key] ?? '').trim();
    return <Fragment key={f.key}>
      <Field id={id} label={label} required={f.required} hint={keptHint ?? hint} error={error} errorRole="none"
        describedBy={aboutKey ? `posture-${kind}-fix-error` : undefined} invalid={aboutKey || undefined}>
        <Input type={f.kind === 'secret' ? 'password' : 'text'} autoComplete="off" spellCheck={false} placeholder={f.placeholder}
          value={values[f.key] ?? ''} onChange={e => set(f.key, e.currentTarget.value)} />
      </Field>
      {f.copyScopes && <CopyScopes scopes={f.copyScopes} />}
      {kind === 'openai' && f.key === 'chatgpt_log_index' && connectDatadog}
    </Fragment>;
  };
  // BL-17: the admin confirmation sits next to the fieldset that triggers it (else after the fields), above Display name.
  const segments = groupFields(fields);
  const privilegeAfter = privilege?.onlyWithFields?.length ? segments.findIndex(seg => seg.fields.some(f => privilege.onlyWithFields!.includes(f.key))) : -1;
  // V104-D-5: take ChatGPT Enterprise off this OpenAI connection. It clears the three fields; saving (with the usual admin-key confirmation) drops them.
  const hadChatGpt = !!connector?.form_values?.chatgpt_workspace_id;
  const removing = hadChatGpt && !(values.chatgpt_workspace_id ?? '').trim() && !(values.compliance_api_key ?? '').trim() && !(values.chatgpt_log_index ?? '').trim();
  // The button stays mounted (focus is never dropped) and turns into its own undo; one status line is mounted from the start.
  const toggleChatGpt = () => {
    if (removing) { setValues(prev => ({ ...prev, chatgpt_workspace_id: initial.chatgpt_workspace_id ?? '', chatgpt_log_index: initial.chatgpt_log_index ?? '' })); return; }
    setValues(prev => ({ ...prev, chatgpt_workspace_id: '', compliance_api_key: '', chatgpt_log_index: '' }));
    setErrors(prev => { const n = { ...prev }; delete n.chatgpt_workspace_id; delete n.compliance_api_key; delete n.chatgpt_log_index; return n; });
  };
  const removeChatGpt = hadChatGpt && <div>
    <Button variant="ghost" onClick={toggleChatGpt}>{removing ? 'Keep ChatGPT Enterprise' : 'Remove ChatGPT Enterprise'}</Button>
    <p className="v75-muted" role="status">{removing ? 'ChatGPT Enterprise will be removed when you save.' : ''}</p>
  </div>;
  const privilegeConfirm = privilege && <PrivilegeConfirm id={`posture-${kind}-privilege`} kind={kind} privilege={privilege} checked={privilegeOk} error={privilegeError} inputRef={privilegeBox} announceError={false}
    onChange={v => { setPrivilegeOk(v); if (v) setPrivilegeError(undefined); }} />;
  return <form ref={form} className="v96-form" onSubmit={submit} noValidate autoComplete="off">
    {!connector && !privilege && kind !== 'vuln_scanner' && <div><Badge className="v75-status neutral"><LockGlyph /> Read-only access</Badge></div>}
    {kind === 'azure_openai' && !connector && <p className="v75-muted">Have a Microsoft admin? <button type="button" className="v96-posture-link"
      onClick={() => { setConsentSubs(values.subscription_ids ?? ''); switchedToApp.current = false; setBackToConsent(true); setAzureMode('consent'); }}>Connect with Microsoft instead</button></p>}
    {kind === 'openai' && !connector && intent === 'add-chatgpt' && <p className="v75-muted">ChatGPT Enterprise is added to an OpenAI connection, so the OpenAI admin key comes first.</p>}
    {!connector && privilege && <div><Badge className="v75-status warning"><StatusGlyph kind="warning" /> {privilegeBadgeLabel(kind)}</Badge></div>}
    {connector && connector.last_error_message && <Alert variant="error" id={`posture-${kind}-fix-error`}><ErrorText text={connector.last_error_message} /></Alert>}
    {wifUnavailable && !isKeyAuth(values) && <div ref={fallbackBtn}><Alert variant="info" title={kind === 'gcp' ? "Workload identity isn't available here yet" : "Keyless sign-in isn't available here yet"}>
      <Button variant="secondary" onClick={() => { setWifUnavailable(false); setAuth('sa_key'); }}>Use a service account key instead</Button>
    </Alert></div>}
    <div role="status" aria-live="polite" className="sr-only">{formLive}</div>
    {formErrors.length > 0 && <Alert variant="error">{formErrors.length === 1 ? formErrors[0] : <ul className="v96-errlist">{formErrors.map(m => <li key={m}>{m}</li>)}</ul>}</Alert>}
    {segments.map((seg, i) => <Fragment key={seg.group?.id ?? seg.fields[0]!.key}>{!seg.group ? seg.fields.map(renderField)
      : seg.group.collapsed
        ? <details className="v101-optional" open={openGroups.has(seg.group.id) || undefined}
            onToggle={e => { const open = (e.currentTarget as HTMLDetailsElement).open; const id = seg.group!.id; setOpenGroups(prev => { const n = new Set(prev); if (open) n.add(id); else n.delete(id); return n; }); }}>
            <summary id={`posture-${kind}-${seg.group.id}-summary`}>{seg.group.legend}</summary>
            <fieldset className="v101-fieldset" aria-labelledby={`posture-${kind}-${seg.group.id}-summary`}>{seg.fields.map(renderField)}
              {kind === 'openai' && seg.group.id === 'chatgpt_enterprise' && removeChatGpt}
            </fieldset>
          </details>
        : <fieldset className="v101-fieldset"><legend>{seg.group.legend}</legend>{seg.fields.map(renderField)}</fieldset>}
      {i === privilegeAfter && privilegeConfirm}
    </Fragment>)}
    {privilege && privilegeAfter < 0 && privilegeConfirm}
    {!connector && <Field id={`posture-${kind}-name`} label="Display name (optional)" error={errors.name}>
      <Input type="text" autoComplete="off" value={values.name ?? ''} placeholder={PROVIDER_LABEL[kind]} onChange={e => set('name', e.currentTarget.value)} />
    </Field>}
    {connector && kind !== 'aws' && !((kind === 'gcp' || kind === 'google_workspace') && !isKeyAuth(values)) && <p className="v75-muted">{kept.size > 0 ? 'Stored keys are never shown. A key left blank keeps its stored value.' : 'Stored credentials are never shown. Enter them again to replace them.'}</p>}
    <div className="v75-actions">
      <Button type="submit" variant="primary" aria-disabled={submitting || undefined}>{submitting ? 'Saving…' : removing ? 'Save and remove ChatGPT Enterprise' : connector ? 'Save and reconnect' : kind === 'vuln_scanner' ? 'Create scan upload' : 'Connect'}</Button>
      <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
    </div>
  </form>;
}

/** The field a connection's error is about, for kinds whose only fix is that key (Datadog refuses unscoped application keys). */
const FIX_ERROR_FIELD: Record<string, string> = { datadog: 'app_key' };

const PRIVILEGE_REQUIRED = 'Tick the box to continue.';

/** What an admin credential can do beyond read-only, as a short phrase (manifest text stays behind “Why”). */
const ADMIN_KEY_CAN: Record<string, string> = {
  anthropic: 'manage members, workspaces and keys',
  openai: 'manage projects, members and keys',
  pinecone: 'create and delete API keys',
};
/** What GAC Pro reads with it. */
const ADMIN_WILL: Record<string, string> = {
  pinecone: 'only read projects, API keys and service accounts',
};
/** The credential's name in the confirmation (default: key). */
const ADMIN_NOUN: Record<string, string> = { pinecone: 'service account' };
/** Forms with more than one key name the privileged one (V101-3-3: OpenAI also takes a ChatGPT compliance key). */
const ADMIN_KEY_LABEL: Record<string, string> = { openai: 'Admin API key' };
export const adminNoun = (kind: string) => ADMIN_NOUN[kind] ?? 'key';

/**
 * ADR-0053 §2 confirmation for a credential broader than read-only: what the
 * key can do, what GAC Pro will do, the re-confirm cadence, why (disclosure)
 * and a required checkbox. Submit sends confirm_privilege only when checked.
 */
export function PrivilegeConfirm({ id, kind, privilege, checked, error, inputRef, onChange, cadence = true, announceError = true }: {
  id: string; kind: string; privilege: PrivilegeDeclaration; checked: boolean; error?: string; inputRef: Ref<HTMLInputElement>; onChange: (v: boolean) => void; cadence?: boolean;
  /** false when the form announces its problems once itself (V104-D-5); the error stays visible and described. */
  announceError?: boolean;
}) {
  return <div className="v101-privilege" role="group" aria-labelledby={`${id}-title`}>
    <dl id={`${id}-title`} className="v101-privilege-facts">
      <div className="v101-privilege-can"><dt><StatusGlyph kind="warning" /> {ADMIN_KEY_LABEL[kind] ? `The ${ADMIN_KEY_LABEL[kind]} can:` : `This ${adminNoun(kind)} can:`}</dt><dd>{ADMIN_KEY_CAN[kind] ?? privilege.vendorLimitation}</dd></div>
      <div><dt><StatusGlyph kind="check" /> GAC Pro will:</dt><dd>{ADMIN_WILL[kind] ?? 'only read usage and settings'}</dd></div>
    </dl>
    {cadence && <p className="v75-muted">Re-confirm every {privilege.revalidateDays} days</p>}
    <details className="v96-scopes v101-why"><summary>{adminNoun(kind) === 'key' ? 'Why an admin key' : 'Why admin access'}</summary><p>{privilege.vendorLimitation}</p><p>{privilege.reason}</p></details>
    <Checkbox ref={inputRef} id={id} checked={checked} onChange={e => onChange(e.currentTarget.checked)}
      aria-required="true" aria-invalid={error ? true : undefined} aria-describedby={error ? `${id}-error` : undefined}
      label={<>{ADMIN_KEY_LABEL[kind] ? `I understand the ${ADMIN_KEY_LABEL[kind]} has admin rights` : `I understand this ${adminNoun(kind)} has admin rights`} <span className="v101-required" aria-hidden="true">*</span></>} />
    {error && <p id={`${id}-error`} className="v101-privilege-error" role={announceError ? 'alert' : undefined}>{error}</p>}
  </div>;
}

/** ADR-0053 §2 / V101-4-3: re-confirm a connector's declared over-privilege (same checkbox as connect). */
function ReconfirmPrivilege({ slug, connector, onClose, onDone }: { slug: string; connector: PostureConnector; onClose: () => void; onDone: (m: string) => void }) {
  const p = connector.privilege!;
  const [ok, setOk] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [failed, setFailed] = useState<string | null>(null);
  const [saved, setSaved] = useState<PostureConnectorPrivilege | null>(null);
  const [busy, setBusy] = useState(false);
  const box = useRef<HTMLInputElement>(null);
  const doneBtn = useRef<HTMLButtonElement>(null);
  useEffect(() => { if (saved) doneBtn.current?.focus(); }, [saved]);
  const due = privilegeDue(saved ?? p);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!ok) { setError(PRIVILEGE_REQUIRED); box.current?.focus(); return; }
    setBusy(true); setFailed(null);
    try {
      const r = await confirmPostureConnectorPrivilege(slug, connector.connector_id);
      setSaved(r.privilege);
      onDone(`${adminNoun(connector.kind) === 'key' ? 'Admin key' : 'Admin access'} re-confirmed for ${PROVIDER_LABEL[connector.kind]}.`);
    } catch (err) { setFailed(err instanceof Error ? err.message : 'That did not work. Please retry.'); }
    finally { setBusy(false); }
  };
  if (saved) return <div className="v96-form">
    <Alert variant="success" title="Re-confirmed">{due.label}</Alert>
    <div className="v75-actions"><Button ref={doneBtn} variant="primary" onClick={onClose}>Done</Button></div>
  </div>;
  return <form className="v96-form" onSubmit={submit} noValidate>
    <p className={`v101-due${due.overdue ? ' overdue' : ''}`}><StatusGlyph kind={due.overdue ? 'warning' : 'clock'} /> {due.label}</p>
    <p className="v75-muted">Last confirmed {relativeTime(p.confirmed_at)}</p>
    <PrivilegeConfirm id={`reconfirm-${connector.connector_id}`} kind={connector.kind} privilege={p} checked={ok} error={error} inputRef={box}
      onChange={v => { setOk(v); if (v) setError(undefined); }} />
    {failed && <Alert variant="error">{failed}</Alert>}
    <div className="v75-actions">
      <Button type="submit" variant="primary" aria-disabled={busy || undefined}>{busy ? 'Saving…' : 'Re-confirm'}</Button>
      <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
    </div>
  </form>;
}

/** What Microsoft’s consent returned with, in plain words, with the next step (ADR-0062). */
type ConsentAction = 'invite' | 'own-app' | 'support' | 'open-workspace';
const AZURE_CONSENT_MESSAGE: Record<string, { variant: 'error' | 'info'; text: string; actions?: ConsentAction[] }> = {
  declined: { variant: 'info', text: 'Consent was cancelled in Microsoft. Nothing was connected.' },
  not_admin: { variant: 'error', text: 'Approving GAC Pro needs a Microsoft Global, Cloud Application or Application Administrator. Ask them to join this workspace as an admin and connect Azure OpenAI.', actions: ['invite', 'own-app'] },
  other_user: { variant: 'error', text: 'That Microsoft sign-in was started by someone else in GAC Pro. Start it again yourself.' },
  tenant_in_use: { variant: 'error', text: 'This Microsoft tenant is connected to another GAC Pro workspace. An owner there can disconnect it, or support can release it.', actions: ['support', 'own-app'] },
  failed: { variant: 'error', text: 'Microsoft sign-in could not be completed. Please try again.' },
  expired: { variant: 'error', text: 'That Microsoft sign-in took too long or was already used. Please start again.' },
  signed_out: { variant: 'error', text: 'You were signed out of GAC Pro during Microsoft sign-in. Please start again.' },
  unavailable: { variant: 'info', text: 'Connecting with Microsoft isn’t available here yet.', actions: ['own-app'] },
};

const SUBSCRIPTION_RX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const consentSubsKey = (slug: string) => `gacpro:azure-consent-subs:${slug}`;
/** A subscription's Access control (IAM) blade in the Azure portal. */
const azurePortalIam = (sub: string) => `https://portal.azure.com/#@/resource/subscriptions/${sub}/users`;
const AZURE_SUBSCRIPTIONS_URL = 'https://portal.azure.com/#view/Microsoft_Azure_Billing/SubscriptionsBladeV2';

/**
 * ADR-0062 / V101-3-9: connect Azure OpenAI with Microsoft admin consent. The
 * admin approves GAC Pro's app in Microsoft; no secret is entered or stored.
 * Subscription IDs survive the round trip to Microsoft (session storage, this tab only).
 */
function AzureConsentStart({ slug, consent, consentWorkspace, prefill, returned, onUseOwnApp, onClose }: {
  slug: string; consent?: string; prefill?: string;
  /** V112-2-8: set only when the caller is a member of the workspace that holds the tenant. */
  consentWorkspace?: { slug: string; name: string };
  /** Back from the own-app form: take focus and say so. */
  returned?: boolean;
  /** Switch to the own-app form, carrying the subscription IDs typed so far. */
  onUseOwnApp: (subs: string) => void; onClose: () => void;
}) {
  const [subs, setSubs] = useState(() => {
    if (prefill) return prefill;
    try { return window.sessionStorage.getItem(consentSubsKey(slug)) ?? ''; } catch { return ''; }
  });
  const [error, setError] = useState<string | undefined>();
  const [formError, setFormError] = useState<{ text: string; actions?: ConsentAction[] } | null>(null);
  const [busy, setBusy] = useState(false);
  // V112-2-8: a tenant held by the caller's own other workspace links there instead of asking support.
  const result = consent === 'tenant_in_use' && consentWorkspace
    ? { variant: 'error' as const, text: `This Microsoft tenant is connected to your workspace “${consentWorkspace.name}”. Manage or disconnect it there.`, actions: ['open-workspace', 'own-app'] as ConsentAction[] }
    : consent ? AZURE_CONSENT_MESSAGE[consent] : undefined;
  const resultBox = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLDivElement>(null);
  const [returnedLive, setReturnedLive] = useState('');
  useEffect(() => {
    if (!returned) return;
    input.current?.querySelector('input')?.focus();
    const t = window.setTimeout(() => setReturnedLive('Showing Microsoft admin consent.'), 50);
    return () => window.clearTimeout(t);
  }, [returned]);
  // Returning from Microsoft: put the result first so it is read out.
  // The Drawer focuses its first field on open; take focus after it so the result is read first.
  useEffect(() => {
    if (!result && !formError) return;
    const t = window.setTimeout(() => resultBox.current?.focus(), 60);
    return () => window.clearTimeout(t);
  }, [result, formError]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const ids = splitFormList(subs);
    const bad = ids.find(id => !SUBSCRIPTION_RX.test(id));
    const problem = ids.length === 0 ? 'Subscription IDs is required.' : ids.length > 20 ? 'Up to 20 subscriptions.'
      : bad ? `“${bad.slice(0, 40)}” is not a subscription ID (32 hex characters with dashes).` : undefined;
    if (problem) { setError(problem); input.current?.querySelector('input')?.focus(); return; }
    setError(undefined); setFormError(null); setBusy(true);
    try { window.sessionStorage.setItem(consentSubsKey(slug), ids.join(', ')); } catch { /* per-tab convenience only */ }
    try {
      const r = await startAzureConsent(slug, { subscription_ids: ids });
      window.location.assign(r.authorize_url);
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiError && err.status === 503) { setFormError({ text: AZURE_CONSENT_MESSAGE.unavailable!.text, actions: ['own-app'] }); return; }
      setFormError({ text: err instanceof Error ? err.message : 'Microsoft sign-in could not start. Please try again.' });
    }
  };
  // BL-18: the invite link opens the invite form with Admin chosen (an owner is still the one who can send it).
  const supportHref = `mailto:support@gacpro.io?subject=${encodeURIComponent('Release a Microsoft tenant from another GAC Pro workspace')}&body=${encodeURIComponent(`Workspace: ${slug}\nPlease release our Microsoft tenant so we can connect Azure OpenAI here.`)}`;
  const actionFor = (a: ConsentAction) => a === 'invite'
    ? <Link className="v96-posture-link" to={`/w/${slug}/app/settings/team?invite_role=admin`}>Go to Team & assignments</Link>
    : a === 'support' ? <><a className="v96-posture-link" href={supportHref}>Ask support to release it</a> (support@gacpro.io)</>
    : a === 'open-workspace' && consentWorkspace ? <Link className="v96-posture-link" to={`/w/${encodeURIComponent(consentWorkspace.slug)}/app/setup`}>Open {consentWorkspace.name}</Link>
    : <button type="button" className="v96-posture-link" onClick={() => onUseOwnApp(subs)}>Use your own app registration</button>;
  const shown = formError ?? (result ? { text: result.text, actions: result.actions } : null);
  return <form className="v96-form" onSubmit={submit} noValidate autoComplete="off">
    <div><Badge className="v75-status neutral"><LockGlyph /> Read-only access</Badge></div>
    {shown && <div ref={resultBox} tabIndex={-1} className="v101-consent-result">
      <Alert variant={formError || result?.variant === 'error' ? 'error' : 'info'}>{shown.text}{shown.actions && <div className="v96-error-actions">{shown.actions.map(a => <span key={a}>{actionFor(a)}</span>)}</div>}</Alert>
    </div>}
    <div role="status" className="sr-only">{returnedLive}</div>
    <p className="v75-muted">An Azure admin approves GAC Pro in Microsoft. You don’t enter or store a secret.</p>
    <div ref={input}>
      <Field id="posture-azure_openai-consent-subs" label="Subscription IDs" required error={error}
        hint={<>Comma-separated, up to 20. Format: 00000000-0000-0000-0000-000000000000 · <a href={AZURE_SUBSCRIPTIONS_URL} target="_blank" rel="noreferrer">Find your subscription IDs<span className="sr-only"> (opens in a new tab)</span></a></>}>
        <Input type="text" autoComplete="off" spellCheck={false} value={subs} onChange={e => { setSubs(e.currentTarget.value); if (error) setError(undefined); }} />
      </Field>
    </div>
    <div className="v75-actions">
      <Button type="submit" variant="primary" aria-disabled={busy || undefined}>{busy ? 'Opening Microsoft…' : 'Continue to Microsoft'}</Button>
      <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
    </div>
    {!shown?.actions?.includes('own-app') && <p className="v75-muted">No Microsoft admin? <button type="button" className="v96-posture-link" onClick={() => onUseOwnApp(subs)}>Use your own app registration instead</button></p>}
  </form>;
}

/** ADR-0062: after consent, the customer assigns read roles to GAC Pro's app on each subscription. */
function AzureAccessSetup({ connector, check }: { connector: PostureConnector; check: (showSteps: () => void) => ReactNode }) {
  const [copied, setCopied] = useTransient();
  const [sent, setSent] = useTransient();
  // BL-18: the roles can be assigned in the Azure portal or with the Azure CLI.
  const [method, setMethod] = useState<'cli' | 'portal'>('cli');
  const steps = useRef<HTMLDetailsElement>(null);
  const copyBtn = useRef<HTMLDivElement>(null);
  const appId = connector.azure_app_id ?? '';
  const subs = splitFormList(connector.form_values?.subscription_ids ?? '');
  const ready = !!appId && subs.length > 0;
  const commands = subs.flatMap(sub => ['Reader', 'Monitoring Reader'].map(role =>
    `az role assignment create --assignee ${appId} --role "${role}" --scope /subscriptions/${sub}`)).join('\n');
  const copy = async () => {
    setCopied('');
    if (!ready) { window.setTimeout(() => setCopied('The subscription list or GAC Pro’s app ID is missing. Reconnect with Microsoft.'), 50); return; }
    try { await navigator.clipboard.writeText(commands); window.setTimeout(() => setCopied('Commands copied.'), 50); }
    catch { window.setTimeout(() => setCopied('Copy was unavailable. Open “Show commands” and copy the text.'), 50); }
  };
  // A message to forward to whoever owns the subscriptions (they may not use GAC Pro).
  const instructions = [
    'Please give GAC Pro read access to these Azure subscriptions.',
    `GAC Pro application (client) ID: ${appId}`,
    `Subscriptions: ${subs.join(', ')}`,
    'Assign the roles Reader and Monitoring Reader to that application on each subscription.',
    'Assigning roles needs the Owner or User Access Administrator role on the subscription.',
    '',
    'Azure CLI:',
    commands,
  ].join('\n');
  const copyInstructions = async () => {
    setSent('');
    if (!ready) { window.setTimeout(() => setSent('The subscription list or GAC Pro’s app ID is missing. Reconnect with Microsoft.'), 50); return; }
    try { await navigator.clipboard.writeText(instructions); window.setTimeout(() => setSent('Instructions copied.'), 50); }
    catch { window.setTimeout(() => setSent('Copy was unavailable. Open “Show commands” and copy the text.'), 50); }
  };
  const showSteps = () => {
    const open = () => { if (steps.current) steps.current.open = true; copyBtn.current?.querySelector('button')?.focus(); };
    if (method === 'cli') open(); else { setMethod('cli'); window.setTimeout(open, 0); }
  };
  return <div className="v96-form">
    {connector.last_error_message && <Alert variant="error"><ErrorText text={connector.last_error_message} /></Alert>}
    <Segmented label="Setup method" value={method} onChange={v => { setMethod(v); setCopied(''); setSent(''); }}
      options={[{ value: 'cli', label: 'Azure CLI' }, { value: 'portal', label: 'Azure portal' }]} />
    <p className="v75-muted">Assigning roles needs the Owner or User Access Administrator role on each subscription.</p>
    <div className="v75-actions">
      <Button variant="secondary" onClick={() => void copyInstructions()}>Copy instructions to send</Button>
      <span role="status" className="v75-muted">{sent}</span>
    </div>
    {method === 'portal' ? <ol className="v96-steps">
      <li>In the Azure portal, open <strong>Access control (IAM)</strong> for each subscription:
        <ul className="v101-policies">{subs.map(sub => <li key={sub}><a href={azurePortalIam(sub)} target="_blank" rel="noreferrer">{sub}<span className="sr-only"> (opens in a new tab)</span></a></li>)}</ul>
      </li>
      <li>Choose <strong>Add</strong>, then <strong>Add role assignment</strong>. Pick <strong>Reader</strong>, choose <strong>User, group, or service principal</strong>, search for GAC Pro’s application ID <code>{appId || '(missing)'}</code>, and save. Repeat with <strong>Monitoring Reader</strong>.</li>
      <li>Check the connection below. Azure can take a few minutes to apply new roles.</li>
    </ol> : <ol className="v96-steps">
      <li>A subscription owner gives GAC Pro’s app the <strong>Reader</strong> and <strong>Monitoring Reader</strong> roles on each subscription (Azure Cloud Shell or the Azure CLI).
        <div ref={copyBtn} className="v75-actions v101-step-actions">
          <Button variant="secondary" onClick={() => void copy()}>Copy Azure CLI commands</Button>
          <span role="status" className="v75-muted">{copied}</span>
        </div>
        <details ref={steps} className="v96-scopes">
          <summary>Show commands</summary>
          {ready
            ? <pre className="v96-code" tabIndex={0} role="region" aria-label="Azure CLI role assignments">{commands}</pre>
            : <p className="v75-muted">The subscription list or GAC Pro’s app ID is missing. Reconnect with Microsoft.</p>}
        </details>
      </li>
      <li>Check the connection below. Azure can take a few minutes to apply new roles.</li>
    </ol>}
    {check(showSteps)}
  </div>;
}

/** ADR-0062: confirm before releasing the tenant (reconnecting needs an Azure admin again). */
/** V112-2-6 (BL-25): remove a paused or disconnected connection; its past checks and evidence stay. */
function RemoveConnection({ slug, connector, onClose, onDone }: { slug: string; connector: PostureConnector; onClose: () => void; onDone: (m: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = async () => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      await removePostureConnector(slug, connector.connector_id);
      onDone('Connection removed. Its past evidence stays.');
    } catch (err) {
      setBusy(false);
      setError(err instanceof Error ? err.message : 'The connection could not be removed. Please retry.');
    }
  };
  return <div className="v96-form">
    {error && <Alert variant="error">{error}</Alert>}
    <p>GAC Pro deletes the stored credentials and this connection leaves the list. The checks it already recorded stay as evidence. To read this account again, connect it again.</p>
    <div className="v75-actions">
      <Button variant="danger" aria-disabled={busy || undefined} onClick={() => void confirm()}>{busy ? 'Removing…' : 'Remove'}</Button>
      <Button variant="ghost" onClick={onClose}>Cancel</Button>
    </div>
  </div>;
}

function AzureDisconnect({ slug, connector, onClose, onDone }: { slug: string; connector: PostureConnector; onClose: () => void; onDone: (m: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = async () => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      await disconnectAzureConsent(slug, connector.connector_id);
      onDone('Azure OpenAI disconnected. Your Azure admin can also remove GAC Pro from Enterprise applications.');
    } catch (err) {
      setBusy(false);
      setError(err instanceof Error ? err.message : 'The connection could not be disconnected. Please retry.');
    }
  };
  return <div className="v96-form">
    {error && <Alert variant="error">{error}</Alert>}
    <p>GAC Pro stops reading this Azure tenant. Its past evidence stays. To connect again, an Azure admin approves GAC Pro in Microsoft again.</p>
    <div className="v75-actions">
      <Button variant="danger" aria-disabled={busy || undefined} onClick={() => void confirm()}>{busy ? 'Disconnecting…' : 'Disconnect'}</Button>
      <Button variant="ghost" onClick={onClose}>Cancel</Button>
    </div>
  </div>;
}

function RoleSetup({ slug, connector, initial, check, onClose }: { slug: string; connector: PostureConnector; initial?: AwsRoleTemplate; check: ReactNode; onClose: () => void }) {
  const [template, setTemplate] = useState<AwsRoleTemplate | null>(initial ?? null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState('');
  const [method, setMethod] = useState<'console' | 'terraform'>('console');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (initial) return;
    let active = true;
    setError(null);
    getAwsRoleTemplate(slug, connector.connector_id)
      .then(r => { if (active) setTemplate(r.role_template); })
      .catch(e => { if (active) setError(e instanceof Error ? e.message : 'The role setup could not load.'); });
    return () => { active = false; };
  }, [slug, connector.connector_id, initial, attempt]);
  const trust = template ? JSON.stringify(template.trust_policy, null, 2) : '';
  const copy = async () => {
    setCopied('');
    try { await navigator.clipboard.writeText(trust); window.setTimeout(() => setCopied('Trust policy copied.'), 50); }
    catch { window.setTimeout(() => setCopied('Copy was unavailable. Open “Show policy” and copy the text.'), 50); }
  };
  if (error) return <div className="v96-form"><Alert variant="error"><p>{error}</p><Button onClick={() => setAttempt(n => n + 1)}>Retry</Button></Alert><div className="v75-actions"><Button variant="ghost" onClick={onClose}>Close</Button></div></div>;
  if (!template) return <Spinner label="Loading role setup" />;
  // The role name only when the saved target is a role ARN (never an account id shown as a role).
  const roleName = /^arn:aws[a-z-]*:iam::\d{12}:role\//.test(connector.target ?? '') ? (template.role_name ?? connector.target!.split('/').pop()) : null;
  const copyTf = async () => {
    setCopied('');
    try { await navigator.clipboard.writeText(template.terraform ?? ''); window.setTimeout(() => setCopied('Terraform copied.'), 50); }
    catch { window.setTimeout(() => setCopied('Copy was unavailable. Open “Show Terraform” and copy the text.'), 50); }
  };
  const tf = !!template.terraform && method === 'terraform';
  return <div className="v96-form">
    {connector.last_error_message && <Alert variant="error"><ErrorText text={connector.last_error_message} /></Alert>}
    {template.terraform && <Segmented label="Setup method" value={method} onChange={v => { setMethod(v); setCopied(''); }}
      options={[{ value: 'console', label: 'Set up in console' }, { value: 'terraform', label: 'Use Terraform' }]} />}
    {tf ? <ol className="v96-steps">
      <li>Apply the Terraform{roleName ? <> for the role <code>{roleName}</code></> : null}.
        <div className="v75-actions v101-step-actions">
          <Button variant="secondary" onClick={() => void copyTf()}>Copy Terraform</Button>
          <span role="status" className="v75-muted">{copied}</span>
        </div>
        <details className="v96-scopes">
          <summary>Show Terraform</summary>
          <pre className="v96-code" tabIndex={0} role="region" aria-label="Terraform for the AWS role">{template.terraform}</pre>
        </details>
      </li>
      <li>Check the connection below.</li>
    </ol> : <ol className="v96-steps">
      {template.oidc_provider && !template.role_exists && <li>In AWS IAM, add an OpenID Connect identity provider: URL <code>{template.oidc_provider.url}</code>, audience <code>{template.oidc_provider.client_id_list[0]}</code>.</li>}
      <li>In AWS IAM, {template.role_exists ? 'open' : 'create'} the role{roleName ? <> <code>{roleName}</code></> : ' you entered'}{template.role_exists ? '' : ' (trusted entity: web identity)'}.</li>
      <li>{template.role_exists ? 'Replace its trust policy with the copied policy.' : 'Use the copied policy as its trust policy.'}
        <div className="v75-actions v101-step-actions">
          <Button variant="secondary" onClick={() => void copy()}>Copy trust policy</Button>
          <span role="status" className="v75-muted">{copied}</span>
        </div>
        <details className="v96-scopes">
          <summary>Show policy</summary>
          <pre className="v96-code" tabIndex={0} role="region" aria-label="AWS trust policy JSON">{trust}</pre>
        </details>
      </li>
      <li>Attach these read-only policies:
        <ul className="v101-policies">{template.managed_policies.map(pol => <li key={pol}><code>{pol.split('/').pop()}</code></li>)}</ul>
      </li>
      {(template.inline_policies?.length ?? 0) > 0 && <li>Add these inline policies, each with its name:
        <ul className="v101-policies">{template.inline_policies!.map(p => <li key={p.name}><InlinePolicy name={p.name} policy={p.policy} /></li>)}</ul>
      </li>}
      <li>Check the connection below.</li>
    </ol>}
    {check}
  </div>;
}

/** One AWS inline policy: its name, a copy button for the JSON and the JSON under a disclosure. */
function InlinePolicy({ name, policy, showName = true }: { name: string; policy: unknown; showName?: boolean }) {
  const [status, setStatus] = useTransient();
  const json = JSON.stringify(policy, null, 2);
  const copy = async () => {
    setStatus('');
    try { await navigator.clipboard.writeText(json); window.setTimeout(() => setStatus('Policy copied.'), 50); }
    catch { window.setTimeout(() => setStatus('Copy was unavailable. Open “Show policy” and copy the text.'), 50); }
  };
  return <div className="v113-inline-policy">
    {showName && <code>{name}</code>}
    <div className="v75-actions v101-step-actions">
      <Button variant="secondary" compact onClick={() => void copy()}>Copy policy<span className="sr-only"> {name}</span></Button>
      <span role="status" className="v75-muted">{status}</span>
    </div>
    <details className="v96-scopes">
      <summary>Show policy<span className="sr-only"> {name}</span></summary>
      <pre className="v96-code" tabIndex={0} role="region" aria-label={`Inline policy ${name}`}>{json}</pre>
    </details>
  </div>;
}

/** The permissions a manifest scope bump added, as code chips named the way the vendor console names them. */
const regrantChips = (scopes: string[]) => <ul className="v113-regrant-scopes" aria-label="Permissions to add">{scopes.map(s => <li key={s}><code className="v101-chip">{scopeDisplayName(s)}</code></li>)}</ul>;

/**
 * ADR-0102 §6: "Update permissions" — only the scopes the manifest added since the grant, the one place
 * the customer adds them, then the same connection check. The full setup stays one click away.
 */
function RegrantSetup({ slug, connector, tracking, check, onClose, onFullSetup, onNewToken }: {
  slug: string; connector: PostureConnector; tracking?: Tracking; check: ReactNode; onClose: () => void;
  onFullSetup: () => void; onNewToken: () => void;
}) {
  const kind = connector.kind;
  const wif = kind === 'gcp' && connector.auth_mode !== 'sa_key';
  // The live row: after a check, its re-grant state says whether the new permission is in place.
  const live = useQuery({ queryKey: queryKeys.postureConnectors(slug), queryFn: () => listPostureConnectors(slug), enabled: !!slug })
    .data?.connectors.find(x => x.connector_id === connector.connector_id) ?? connector;
  const scopes = (live.regrant ?? connector.regrant)?.scopes ?? [];
  const [aws, setAws] = useState<AwsRoleTemplate | null>(null);
  const [gcp, setGcp] = useState<GcpWifTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useTransient();
  useEffect(() => {
    if (kind !== 'aws' && !wif) return;
    let active = true;
    setError(null);
    (kind === 'aws'
      ? getAwsRoleTemplate(slug, connector.connector_id).then(r => { if (active) setAws(r.role_template); })
      : getGcpWifTemplate(slug, connector.connector_id).then(r => { if (active) setGcp(r.wif_template); }))
      .catch(e => { if (active) setError(e instanceof Error ? e.message : 'The setup could not load.'); });
    return () => { active = false; };
  }, [slug, connector.connector_id, kind, wif, attempt]);
  const newHeading = useRef<HTMLParagraphElement>(null);
  useEffect(() => { newHeading.current?.scrollIntoView?.({ block: 'start' }); }, []);

  // One answer after a run: did the new permission land (CheckSection keeps its button and only reports a failed run).
  const done = tracking?.phase === 'ok' || tracking?.phase === 'partial';
  // Keyed on the run's metric read: only a completed read (`ok`) says the permission is in place.
  const read = live.metrics_read?.status;
  const result = done ? (read === 'ok'
    ? <Alert variant="success" title="Permission added">Usage and guardrail metrics are collected from now on.</Alert>
    : <Alert variant="info" title="Not added yet">{read === 'refused'
      ? <>{scopes.map((s, i) => <Fragment key={s}>{i > 0 && ', '}<code>{scopeDisplayName(s)}</code></Fragment>)} {scopes.length === 1 ? 'is' : 'are'} still refused. New access can take a few minutes to apply.</>
      : 'The metrics could not be read on this run. Check again shortly.'}</Alert>) : null;

  // AWS: the role's metric-read inline policy (the one naming the added CloudWatch actions).
  const metricPolicy = aws?.inline_policies?.find(p => scopes.some(s => JSON.stringify(p.policy).includes(scopeDisplayName(s))));
  const roleName = /^arn:aws[a-z-]*:iam::\d{12}:role\//.test(connector.target ?? '') ? (aws?.role_name ?? connector.target!.split('/').pop()) : null;
  const govCloud = /^arn:aws-us-gov:/.test(connector.target ?? '');
  const roleUrl = roleName ? `https://console.${govCloud ? 'amazonaws-us-gov' : 'aws.amazon'}.com/iam/home#/roles/details/${encodeURIComponent(roleName)}?section=permissions` : null;
  // GCP keyless: just the grant lines for the added roles. Key-based: the same grant, built from the saved scope and account.
  const scopeMatch = /^(projects|organizations)\/(.+)$/.exec(connector.target ?? '');
  const keyGrants = !wif && kind === 'gcp' && scopeMatch && connector.service_account_email
    ? scopes.map(s => `gcloud ${scopeMatch[1]} add-iam-policy-binding ${scopeMatch[2]} --member=serviceAccount:${connector.service_account_email} --role=${s} --condition=None`) : [];
  const grantLines = gcp ? gcp.commands.filter(l => scopes.some(s => l.includes(s))) : keyGrants;
  const commands = (grantLines.length ? grantLines : gcp?.commands ?? []).join('\n');
  const one = grantLines.length === 1;
  const copyCommands = async () => {
    setStatus('');
    try { await navigator.clipboard.writeText(commands); window.setTimeout(() => setStatus(one ? 'Command copied.' : 'Commands copied.'), 50); }
    catch { window.setTimeout(() => setStatus(`Copy was unavailable. Open “${one ? 'Show command' : 'Show commands'}” and copy the text.`), 50); }
  };
  const named = (list: string[]) => list.map((s, i) => <Fragment key={s}>{i > 0 && ' and '}<code>{scopeDisplayName(s)}</code></Fragment>);
  const commandStep = <>
    <div className="v75-actions v101-step-actions"><Button variant="secondary" compact onClick={() => void copyCommands()}>{one ? 'Copy command' : 'Copy commands'}</Button><span role="status" className="v75-muted">{status}</span></div>
    <details className="v96-scopes"><summary>{one ? 'Show command' : 'Show commands'}</summary>
      <pre className="v96-code" tabIndex={0} role="region" aria-label="Commands for the new permission">{commands}</pre></details>
  </>;

  if (error) return <div className="v96-form"><Alert variant="error"><p>{error}</p><Button onClick={() => setAttempt(n => n + 1)}>Retry</Button></Alert><div className="v75-actions"><Button variant="ghost" onClick={onClose}>Close</Button></div></div>;
  if ((kind === 'aws' && !aws) || (wif && !gcp)) return <Spinner label="Loading setup" />;
  return <div className="v96-form">
    <div className="v113-regrant-new">
      <p ref={newHeading}><strong>{scopes.length === 1 ? 'New permission' : 'New permissions'}</strong> · read-only</p>
      {regrantChips(scopes)}
    </div>
    <ol className="v96-steps" aria-label="Steps to add the permission">
      {kind === 'aws' ? <>
        <li>In AWS IAM, open the role {roleUrl ? vendorLink(roleUrl, roleName!) : 'GAC Pro uses'}.</li>
        <li>{metricPolicy
          ? <>Choose Add permissions, then Create inline policy. Paste this JSON and name it <code>{metricPolicy.name}</code>.<InlinePolicy name={metricPolicy.name} policy={metricPolicy.policy} showName={false} /></>
          : <>Allow {named(scopes)} on all resources.</>}</li>
      </> : kind === 'gcp' ? <>
        <li>Open {vendorLink('https://shell.cloud.google.com', 'Cloud Shell')} as {connector.target?.startsWith('projects/') ? 'a project owner' : 'an organization admin'}.</li>
        <li>{grantLines.length
          ? <>Run {one ? 'this command' : 'these commands'}. {one ? 'It is' : 'They are'} safe to run again.{commandStep}</>
          : <>Grant {named(scopes)} to the service account GAC Pro uses.</>}</li>
      </> : <>
        <li>In Cloudflare, {vendorLink('https://dash.cloudflare.com/profile/api-tokens', 'open API tokens')} and edit the token GAC Pro uses.</li>
        <li>Add the {named(scopes)} permission, then save. The token value stays the same.</li>
      </>}
      <li>Check the connection below.</li>
    </ol>
    <div aria-live="polite">{result}</div>
    {check}
    <div className="v75-actions">
      {kind === 'aws' || wif
        ? <Button variant="ghost" onClick={onFullSetup}>{kind === 'aws' ? 'View full role setup' : 'View all setup commands'}</Button>
        : kind === 'cloudflare_ai_gateway' ? <Button variant="ghost" onClick={onNewToken}>Use a new token instead</Button> : null}
    </div>
  </div>;
}

/** A two-or-more option segmented control (radiogroup; arrow keys move the selection). */
function Segmented<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: Array<{ value: T; label: string }>; onChange: (v: T) => void }) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const move = (i: number) => { const n = (i + options.length) % options.length; onChange(options[n]!.value); refs.current[n]?.focus(); };
  return <div className="v101-segmented" role="radiogroup" aria-label={label}>
    {options.map((o, i) => <button key={o.value} ref={el => { refs.current[i] = el; }} type="button" role="radio" aria-checked={value === o.value}
      tabIndex={value === o.value ? 0 : -1} className="v101-segment" onClick={() => onChange(o.value)}
      onKeyDown={e => { if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); move(i + 1); } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); move(i - 1); } }}>
      {o.label}
    </button>)}
  </div>;
}

function GcpSetup({ slug, connector, initial, retireKey, check, onClose }: {
  slug: string; connector: PostureConnector; initial?: GcpWifTemplate; retireKey?: string[];
  check: (emphasis: 'primary' | 'secondary') => ReactNode; onClose: () => void;
}) {
  const [template, setTemplate] = useState<GcpWifTemplate | null>(initial ?? null);
  const [error, setError] = useState<string | null>(null);
  // One status per copy button; `ran` sticks once the commands were copied so
  // later copies (scopes, delete command) never take emphasis away from Check.
  const [status, setStatus] = useState<{ key: string; msg: string }>({ key: '', msg: '' });
  const [ran, setRan] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (initial) return;
    let active = true;
    setError(null);
    getGcpWifTemplate(slug, connector.connector_id)
      .then(r => { if (active) setTemplate(r.wif_template); })
      .catch(e => { if (active) setError(e instanceof Error ? e.message : 'The setup could not load.'); });
    return () => { active = false; };
  }, [slug, connector.connector_id, initial, attempt]);
  const script = template ? template.commands.join('\n') : '';
  const copyText = async (key: string, text: string, ok: string, show: string) => {
    setStatus({ key, msg: '' });
    if (!text) { window.setTimeout(() => setStatus({ key, msg: 'Nothing to copy yet — reload the setup.' }), 50); return; }
    try {
      await navigator.clipboard.writeText(text);
      if (key === 'commands') setRan(true);
      window.setTimeout(() => setStatus({ key, msg: ok }), 50);
    } catch { window.setTimeout(() => setStatus({ key, msg: `Copy was unavailable. Open “${show}” and copy the text.` }), 50); }
  };
  const said = (key: string) => <span role="status" className="v75-muted">{status.key === key ? status.msg : ''}</span>;
  const copy = () => copyText('commands', script, 'Commands copied.', 'Show commands');
  if (error) return <div className="v96-form"><Alert variant="error"><p>{error}</p><Button onClick={() => setAttempt(n => n + 1)}>Retry</Button></Alert><div className="v75-actions"><Button variant="ghost" onClick={onClose}>Close</Button></div></div>;
  if (!template) return <Spinner label="Loading setup" />;
  const done = ran;
  const workspace = connector.kind === 'google_workspace';
  return <div className="v96-form">
    <ol className="v96-steps">
      {workspace ? <>
        <li>Open <a href="https://shell.cloud.google.com" target="_blank" rel="noreferrer">Cloud Shell</a> as an owner of the host project.</li>
        <li>Paste the commands into Cloud Shell and press Enter (answer Y if asked). They are safe to run again; the last line prints the service account’s client ID (a long number).
          <div className="v75-actions"><Button variant="ghost" onClick={() => void copyText('clientid', template.commands.at(-1) ?? '', 'Client ID command copied.', 'Show commands')}>Copy client ID command</Button>{said('clientid')}</div></li>
        <li>Open <a href="https://admin.google.com/ac/owl/domainwidedelegation" target="_blank" rel="noreferrer">Domain-wide delegation in Google Admin</a>, choose Add new, and paste the client ID and these scopes there — not into Cloud Shell.
          {connector.scopes.length > 0 && <>
            <div className="v75-actions"><Button variant="secondary" onClick={() => void copyText('scopes', connector.scopes.join(','), 'Scopes copied.', 'Show scopes')}>Copy scopes for Google Admin</Button>{said('scopes')}</div>
            <details className="v96-scopes"><summary>Show scopes</summary><pre className="v96-code" tabIndex={0} role="region" aria-label="Domain-wide delegation scopes">{connector.scopes.join(',')}</pre></details>
          </>}</li>
        <li>Check the connection below. New access can take a few minutes to apply.</li>
      </> : <>
        <li>Open <a href="https://shell.cloud.google.com" target="_blank" rel="noreferrer">Cloud Shell</a> as {connector.target?.startsWith('projects/') ? 'a project owner' : 'an organization admin'}.</li>
        <li>Paste and run the commands. They are safe to run again and grant {template.roles.length} read-only roles.</li>
        <li>Check the connection below. New access can take a few minutes to apply.</li>
      </>}
      {retireKey && retireKey.length > 0 && <li>Delete the old key in Google Cloud. GAC Pro deleted its copy; the key works until you delete it.
        <div className="v75-actions"><Button variant="secondary" onClick={() => void copyText('retire', retireKey.join('\n'), 'Delete command copied.', 'Show delete command')}>Copy delete command</Button>{said('retire')}</div>
        <details className="v96-scopes"><summary>Show delete command</summary>
          <pre className="v96-code" tabIndex={0} role="region" aria-label="Command to delete the old key">{retireKey.join('\n')}</pre>
        </details></li>}
    </ol>
    <div className="v75-actions"><Button variant={done ? 'secondary' : 'primary'} onClick={() => void copy()}>Copy commands for Cloud Shell</Button>{said('commands')}</div>
    <details className="v96-scopes">
      <summary>Show commands</summary>
      <pre className="v96-code" tabIndex={0} role="region" aria-label={`${PROVIDER_LABEL[connector.kind]} setup commands`}>{script}</pre>
    </details>
    {check(done ? 'primary' : 'secondary')}
  </div>;
}

const MAX_SCAN_BYTES = 5 * 1024 * 1024;

function ScanUpload({ slug, connector, say, onUploaded, onQueued, onClose }: { slug: string; connector: PostureConnector; say: (m: string) => void; onUploaded: (m: string) => void; onQueued: () => void; onClose: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const [fileError, setFileError] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [confirmEmpty, setConfirmEmpty] = useState(false);
  const [result, setResult] = useState<ScanUploadResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const format = file ? scanFormatFor(file.name) : null;

  const choose = (f: File | undefined) => {
    setResult(null); setError(null); setConfirmEmpty(false); setFileError(undefined);
    if (!f) { setFile(null); return; }
    if (!scanFormatFor(f.name)) { setFile(null); setFileError('Use a .csv or .sarif (.json) scan export.'); return; }
    if (f.size > MAX_SCAN_BYTES) { setFile(null); setFileError('This file is larger than 5 MB. Split the scan or export fewer assets.'); return; }
    setFile(f);
  };
  const reset = () => { setConfirmEmpty(false); setFile(null); setInputKey(k => k + 1); window.setTimeout(() => document.getElementById('posture-scan-file')?.focus(), 0); };

  const send = async (confirm: boolean) => {
    if (submitting) return;
    if (!file || !format) { setFileError('Choose a scan file.'); return; }
    setSubmitting(true); setError(null);
    try {
      const r = await uploadVulnerabilityScan(slug, connector.connector_id, file, format, confirm);
      setConfirmEmpty(false);
      // Render the (empty) result region first, then fill it, so it is announced.
      window.setTimeout(() => setResult(r), 50);
      if (r.status === 'processing') { onQueued(); onUploaded('Scan received. Findings are still processing.'); say('Scan received. Findings are still processing.'); }
      else { onUploaded(`Scan processed: ${r.vulnerabilities} vulnerabilities.`); say(`Scan processed: ${r.vulnerabilities} vulnerabilities.`); }
    } catch (err) {
      const body = err instanceof ApiError ? err.body as { code?: string } | undefined : undefined;
      if (body?.code === 'empty_scan') setConfirmEmpty(true);
      else if (err instanceof ApiError && err.status === 413) setFileError('This file is larger than 5 MB.');
      else setError(err instanceof Error ? err.message : 'The scan could not be uploaded. Please retry.');
    } finally { setSubmitting(false); }
  };

  return <div className="v96-form">
    <Field id="posture-scan-file" label="Scan file" required error={fileError} hint={file ? `${file.name} · ${format === 'csv' ? 'CSV' : 'SARIF'}` : 'CSV or SARIF, up to 5 MB.'}>
      <Input key={inputKey} type="file" accept=".csv,.sarif,.json,text/csv,application/json" onChange={e => choose(e.currentTarget.files?.[0])} />
    </Field>
    {error && <Alert variant="error">{error}</Alert>}
    {confirmEmpty && <Alert variant="warning" title="This scan has no findings">
      <p>Uploading it closes every open finding from this scanner.</p>
      <div className="v75-actions"><Button variant="ghost" onClick={reset}>Choose another file</Button></div>
    </Alert>}
    {result && <Alert variant="success" title={result.status === 'processing' ? 'Scan received — still processing' : 'Scan processed'}>
      <p>{result.vulnerabilities} vulnerabilities · {result.findings.opened} findings opened · {result.findings.updated} updated · {result.findings.closed} closed</p>
    </Alert>}
    <div className="v75-actions">
      {/* One upload button across states: Upload scan → Upload anyway (empty scan) → Upload another scan. */}
      <Button variant={result ? 'secondary' : 'primary'} aria-disabled={submitting || (!file && !result) || undefined}
        onClick={() => { if (submitting) return; if (result) { setResult(null); reset(); return; } void send(confirmEmpty); }}>
        {submitting ? 'Uploading…' : result ? 'Upload another scan' : confirmEmpty ? 'Upload anyway' : 'Upload scan'}
      </Button>
      <Button variant="ghost" onClick={onClose}>{result ? 'Done' : 'Cancel'}</Button>
    </div>
  </div>;
}
